import type { JSX } from 'react';
import type { Figure as FigureData } from '@yuan-math/curriculum';

/**
 * Declarative figures, drawn as SVG.
 *
 * Content files describe *what* a figure shows — "a bar in 4 parts with 3
 * shaded" — and this component decides how to draw it. That keeps content free
 * of coordinates, makes every diagram theme-aware and crisp at any size, and
 * means improving the visual language is one change here rather than a hundred
 * changes in the curriculum.
 */

export function Figure({ figure }: { figure: FigureData }): JSX.Element | null {
  switch (figure.kind) {
    case 'fraction-bar':
      return <FractionBars bars={[figure]} />;
    case 'fraction-bars':
      return <FractionBars bars={figure.bars} />;
    case 'number-line':
      return <NumberLine {...figure} />;
    case 'array':
      return <DotArray {...figure} />;
    case 'area-model':
      return <AreaModel {...figure} />;
    case 'grid':
      return <UnitGrid {...figure} />;
    case 'clock':
      return <ClockFace {...figure} />;
    case 'shape':
      return <Shape {...figure} />;
    case 'place-value':
      return <PlaceValue {...figure} />;
    case 'tape-diagram':
      return <TapeDiagram {...figure} />;
    case 'coordinate-grid':
      return <CoordinateGrid {...figure} />;
    case 'dot-plot':
      return <DotPlot {...figure} />;
    case 'bar-chart':
      return <BarChart {...figure} />;
    case 'histogram':
      return <Histogram {...figure} />;
    case 'box-plot':
      return <BoxPlot {...figure} />;
    case 'prism':
      return <Prism {...figure} />;
    case 'solid':
      return <Solid {...figure} />;
    case 'parallel-lines':
      return <ParallelLines {...figure} />;
    case 'polygon':
      return <RegularPolygon {...figure} />;
    case 'circle-diagram':
      return <CircleDiagram {...figure} />;
    case 'circle-sector':
      return <CircleSector {...figure} />;
    case 'construction':
      return <ConstructionFigure {...figure} />;
    case 'angle':
      return <AngleFigure {...figure} />;
    case 'symmetry':
      return <SymmetryFigure {...figure} />;
    case 'unit-circle':
      return <UnitCircle {...figure} />;
    case 'function-graph':
      return <FunctionGraph {...figure} />;
    case 'piecewise-graph':
      return <PiecewiseGraph {...figure} />;
    case 'oblique-triangle':
      return <ObliqueTriangle {...figure} />;
    case 'conic':
      return <Conic {...figure} />;
    case 'vector-diagram':
      return <VectorDiagram {...figure} />;
    case 'pascal-triangle':
      return <PascalTriangle {...figure} />;
    case 'normal-curve':
      return <NormalCurve {...figure} />;
    case 'density':
      return <DensityCurve {...figure} />;
    case 'venn':
      return <VennDiagram {...figure} />;
    case 'probability-tree':
      return <ProbabilityTree {...figure} />;
    default:
      return null;
  }
}

/* -------------------------------------------------------------------------- */

function FractionBars({ bars }: { bars: { parts: number; shaded: number; label?: string }[] }): JSX.Element {
  const width = 320;
  const barHeight = 44;
  const gap = 14;
  const labelWidth = bars.some((bar) => bar.label) ? 58 : 0;
  const height = bars.length * (barHeight + gap) - gap;

  return (
    <svg viewBox={`0 0 ${width + labelWidth} ${height}`} className="figure-svg" role="img"
         aria-label={bars.map((bar) => `${bar.shaded} of ${bar.parts} parts shaded`).join('; ')}>
      {bars.map((bar, barIndex) => {
        const y = barIndex * (barHeight + gap);
        const cellWidth = width / bar.parts;
        return (
          <g key={barIndex}>
            {Array.from({ length: bar.parts }, (_, i) => (
              <rect
                key={i}
                x={i * cellWidth}
                y={y}
                width={cellWidth}
                height={barHeight}
                fill={i < bar.shaded ? 'var(--accent)' : 'var(--paper-sunken)'}
                stroke="var(--rule-strong)"
                strokeWidth={1.5}
              />
            ))}
            {bar.label ? (
              <text x={width + 10} y={y + barHeight / 2 + 5} className="figure-label" fill="var(--ink-muted)">
                {bar.label}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

function NumberLine({
  from,
  to,
  step,
  marks = [],
  labelAs,
  ray,
}: {
  from: number;
  to: number;
  step: number;
  marks?: { at: number; label?: string }[];
  labelAs?: 'fraction';
  ray?: { at: number; direction: 'left' | 'right'; closed: boolean };
}): JSX.Element {
  const width = 360;
  const height = 76;
  const padding = 22;
  const span = to - from || 1;
  const x = (value: number): number => padding + ((value - from) / span) * (width - 2 * padding);

  const ticks: number[] = [];
  // Accumulate in integer counts to avoid floating-point drift on 0.1 steps.
  const count = Math.round(span / step);
  for (let i = 0; i <= count; i += 1) ticks.push(Number((from + i * step).toFixed(6)));

  /*
   * A line in eighths whose ticks read 0.125, 0.25, 0.375 teaches the wrong
   * lesson: the point of putting fractions on a line is that they are numbers
   * *of that name*. Whole values stay whole, so 4/4 still reads as 1.
   */
  const denominator = Math.round(1 / step);
  const tickLabel = (tick: number): string => {
    if (labelAs !== 'fraction' || denominator < 2) return minus(tick);
    const numerator = Math.round(tick * denominator);
    return numerator % denominator === 0 ? minus(numerator / denominator) : `${numerator}/${denominator}`;
  };

  const rayLabel = ray
    ? `, shaded ${ray.direction === 'left' ? 'below' : 'above'} ${minus(ray.at)}${ray.closed ? ' including' : ' not including'} ${minus(ray.at)}`
    : '';

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label={`Number line from ${from} to ${to}${rayLabel}`}>
      <defs>
        <marker id="number-line-arrowhead" markerWidth={8} markerHeight={8} refX={7} refY={4} orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill="var(--accent)" />
        </marker>
      </defs>
      <line x1={padding} y1={38} x2={width - padding} y2={38} stroke="var(--rule-strong)" strokeWidth={2} />
      {ray ? (
        <g>
          <line
            x1={x(ray.at)}
            y1={38}
            x2={ray.direction === 'left' ? padding + 6 : width - padding - 6}
            y2={38}
            stroke="var(--accent)"
            strokeWidth={5}
            markerEnd="url(#number-line-arrowhead)"
          />
          {/* Hollow means "not this one". It is the whole of > against ≥. */}
          <circle
            cx={x(ray.at)}
            cy={38}
            r={7}
            fill={ray.closed ? 'var(--accent)' : 'var(--paper)'}
            stroke="var(--accent)"
            strokeWidth={2.5}
          />
        </g>
      ) : null}
      {ticks.map((tick) => (
        <g key={tick}>
          <line x1={x(tick)} y1={31} x2={x(tick)} y2={45} stroke="var(--rule-strong)" strokeWidth={1.5} />
          {ticks.length <= 12 ? (
            <text x={x(tick)} y={62} textAnchor="middle" className="figure-label" fill="var(--ink-faint)">
              {tickLabel(tick)}
            </text>
          ) : null}
        </g>
      ))}
      {marks.map((mark) => (
        <g key={`${mark.at}-${mark.label ?? ''}`}>
          <circle cx={x(mark.at)} cy={38} r={6} fill="var(--accent)" />
          {mark.label ? (
            <text x={x(mark.at)} y={20} textAnchor="middle" className="figure-label-strong" fill="var(--accent)">
              {mark.label}
            </text>
          ) : null}
        </g>
      ))}
    </svg>
  );
}

function DotArray({ rows, columns, label }: { rows: number; columns: number; label?: string }): JSX.Element {
  const cell = 26;
  const padding = 10;
  const width = columns * cell + padding * 2;
  const height = rows * cell + padding * 2 + (label ? 22 : 0);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img" aria-label={`${rows} rows of ${columns}`}>
      {Array.from({ length: rows }, (_, r) =>
        Array.from({ length: columns }, (_, c) => (
          <circle key={`${r}-${c}`} cx={padding + c * cell + cell / 2} cy={padding + r * cell + cell / 2} r={8} fill="var(--accent)" />
        )),
      )}
      {label ? (
        <text x={width / 2} y={height - 6} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}

function AreaModel({
  rows,
  columns,
  shadedRows,
  shadedColumns,
}: {
  rows: number;
  columns: number;
  shadedRows: number;
  shadedColumns: number;
}): JSX.Element {
  const cell = 40;
  const width = columns * cell;
  const height = rows * cell;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label={`Area model: ${shadedRows} of ${rows} rows by ${shadedColumns} of ${columns} columns`}>
      {Array.from({ length: rows }, (_, r) =>
        Array.from({ length: columns }, (_, c) => {
          const inColumns = c < shadedColumns;
          const inRows = r < shadedRows;
          const fill = inColumns && inRows ? 'var(--accent)' : inColumns || inRows ? 'var(--accent-soft)' : 'var(--paper-sunken)';
          return (
            <rect key={`${r}-${c}`} x={c * cell} y={r * cell} width={cell} height={cell} fill={fill}
                  stroke="var(--rule-strong)" strokeWidth={1} />
          );
        }),
      )}
    </svg>
  );
}

function UnitGrid({ width: cols, height: rows, shaded }: { width: number; height: number; shaded?: [number, number][] }): JSX.Element {
  const cell = 26;
  const w = cols * cell;
  const h = rows * cell;
  const shadedSet = new Set((shaded ?? []).map(([x, y]) => `${x},${y}`));
  const shadeAll = shaded === undefined;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="figure-svg" role="img" aria-label={`${cols} by ${rows} grid of unit squares`}>
      {Array.from({ length: rows }, (_, r) =>
        Array.from({ length: cols }, (_, c) => (
          <rect
            key={`${r}-${c}`}
            x={c * cell}
            y={r * cell}
            width={cell}
            height={cell}
            fill={shadeAll || shadedSet.has(`${c},${r}`) ? 'var(--accent-soft)' : 'var(--paper-sunken)'}
            stroke="var(--rule-strong)"
            strokeWidth={1}
          />
        )),
      )}
    </svg>
  );
}

/**
 * An analogue clock face.
 *
 * The only figure in the library whose *point* is that one picture is read two
 * ways at once: the short hand names an hour it has not reached yet, and the
 * long hand names five minutes for every numeral it passes. Drawing the minute
 * ticks — not just the twelve numerals — is what makes that legible, so a
 * student can count round rather than guess.
 */
function ClockFace({ hour, minute, label }: { hour: number; minute: number; label?: string }): JSX.Element {
  const size = 220;
  const centre = size / 2;
  const radius = 92;
  // Drawn modulo 12 and 60, so an out-of-range time is still a real clock face.
  const h = ((Math.trunc(hour) % 12) + 12) % 12;
  const m = ((Math.trunc(minute) % 60) + 60) % 60;
  // The hour hand creeps as the minutes pass — a clock that snaps from one
  // numeral to the next teaches a wrong idea about how time is read.
  const hourAngle = (h + m / 60) * 30;

  const point = (degrees: number, length: number): [number, number] => {
    const a = ((degrees - 90) * Math.PI) / 180;
    return [centre + length * Math.cos(a), centre + length * Math.sin(a)];
  };

  const [hx, hy] = point(hourAngle, radius * 0.5);
  const [mx, my] = point(m * 6, radius * 0.8);
  const reading = `${h === 0 ? 12 : h}:${String(m).padStart(2, '0')}`;
  const height = size + (label ? 18 : 0);

  return (
    <svg viewBox={`0 0 ${size} ${height}`} className="figure-svg" role="img" aria-label={`Clock face showing ${reading}`}>
      <circle cx={centre} cy={centre} r={radius} fill="var(--paper-sunken)" stroke="var(--rule-strong)" strokeWidth={2} />
      {Array.from({ length: 60 }, (_, i) => {
        const major = i % 5 === 0;
        const [x1, y1] = point(i * 6, radius - (major ? 11 : 5));
        const [x2, y2] = point(i * 6, radius - 2);
        return (
          <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={major ? 'var(--rule-strong)' : 'var(--rule)'} strokeWidth={major ? 2 : 1} />
        );
      })}
      {Array.from({ length: 12 }, (_, i) => {
        const [x, y] = point((i + 1) * 30, radius - 27);
        return (
          <text key={i} x={x} y={y + 5} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
            {i + 1}
          </text>
        );
      })}
      {/* Short and thick for the hour, long and coloured for the minutes. */}
      <line x1={centre} y1={centre} x2={hx} y2={hy} stroke="var(--ink)" strokeWidth={5} strokeLinecap="round" />
      <line x1={centre} y1={centre} x2={mx} y2={my} stroke="var(--accent)" strokeWidth={3} strokeLinecap="round" />
      <circle cx={centre} cy={centre} r={4} fill="var(--ink)" />
      {label ? (
        <text x={centre} y={height - 4} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}

function Shape({ shape, labels = {} }: { shape: 'rectangle' | 'triangle' | 'circle'; labels?: Record<string, string> }): JSX.Element {
  const width = 300;
  const height = 190;

  if (shape === 'circle') {
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img" aria-label="Circle">
        <circle cx={width / 2} cy={height / 2} r={70} fill="var(--accent-soft)" stroke="var(--accent)" strokeWidth={2} />
        <line x1={width / 2} y1={height / 2} x2={width / 2 + 70} y2={height / 2} stroke="var(--accent)" strokeWidth={2} />
        {labels.radius ? (
          <text x={width / 2 + 35} y={height / 2 - 8} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
            {labels.radius}
          </text>
        ) : null}
      </svg>
    );
  }

  if (shape === 'triangle') {
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img" aria-label="Right triangle">
        <polygon points={`40,150 260,150 40,40`} fill="var(--accent-soft)" stroke="var(--accent)" strokeWidth={2} />
        {/* The square marks the right angle, without which "hypotenuse" means nothing. */}
        {labels.hypotenuse ? (
          <polyline points="40,136 54,136 54,150" fill="none" stroke="var(--ink-muted)" strokeWidth={1.5} />
        ) : null}
        {labels.base ? <text x={150} y={172} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">{labels.base}</text> : null}
        {labels.height ? <text x={22} y={98} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">{labels.height}</text> : null}
        {labels.hypotenuse ? (
          <text x={168} y={88} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">{labels.hypotenuse}</text>
        ) : null}
        {/*
          * The acute angle, drawn at the vertex it belongs to. Trigonometry
          * labels sides *relative to an angle*, so a figure that names three
          * sides and leaves the angle to the prose has hidden the one thing
          * the student has to look at first.
          */}
        {labels.angle ? (
          <text x={230} y={144} textAnchor="end" className="figure-label" fill="var(--ink)">{labels.angle}</text>
        ) : null}
      </svg>
    );
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img" aria-label="Rectangle">
      <rect x={40} y={40} width={220} height={110} fill="var(--accent-soft)" stroke="var(--accent)" strokeWidth={2} />
      {labels.width ? (
        <text x={150} y={172} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">{labels.width}</text>
      ) : null}
      {labels.height ? (
        <text x={22} y={100} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">{labels.height}</text>
      ) : null}
    </svg>
  );
}

function PlaceValue({ digits, places }: { digits: string; places: string[] }): JSX.Element {
  const columnWidth = 88;
  const width = digits.length * columnWidth;
  const height = 96;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img" aria-label={`Place value chart for ${digits}`}>
      {[...digits].map((digit, index) => (
        <g key={index}>
          <rect x={index * columnWidth} y={0} width={columnWidth} height={height} fill={index % 2 === 0 ? 'var(--paper-sunken)' : 'transparent'}
                stroke="var(--rule)" strokeWidth={1} />
          <text x={index * columnWidth + columnWidth / 2} y={24} textAnchor="middle" className="figure-label" fill="var(--ink-faint)">
            {places[index] ?? ''}
          </text>
          <text x={index * columnWidth + columnWidth / 2} y={70} textAnchor="middle" className="figure-digit" fill="var(--ink)">
            {digit}
          </text>
        </g>
      ))}
    </svg>
  );
}

/* --------------------------------------------------------------- middle -- */

/**
 * A tape diagram: the standard picture for a ratio.
 *
 * Every part is drawn the same width across all rows, which is the whole point
 * — it is what makes "3 parts to 2 parts" visible as a comparison rather than
 * as two unrelated numbers.
 */
function TapeDiagram({ rows, unitLabel }: { rows: { label: string; parts: number }[]; unitLabel?: string }): JSX.Element {
  const cell = 40;
  const rowHeight = 38;
  const gap = 12;
  const labelWidth = 74;
  const widest = Math.max(...rows.map((row) => row.parts), 1);
  const width = labelWidth + widest * cell + 8;
  const height = rows.length * (rowHeight + gap) - gap + (unitLabel ? 22 : 0);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label={rows.map((row) => `${row.label}: ${row.parts} parts`).join('; ')}>
      {rows.map((row, rowIndex) => {
        const y = rowIndex * (rowHeight + gap);
        return (
          <g key={row.label}>
            <text x={labelWidth - 10} y={y + rowHeight / 2 + 5} textAnchor="end" className="figure-label" fill="var(--ink-muted)">
              {row.label}
            </text>
            {Array.from({ length: row.parts }, (_, i) => (
              <rect
                key={i}
                x={labelWidth + i * cell}
                y={y}
                width={cell}
                height={rowHeight}
                fill={rowIndex % 2 === 0 ? 'var(--accent)' : 'var(--accent-soft)'}
                stroke="var(--rule-strong)"
                strokeWidth={1.5}
              />
            ))}
          </g>
        );
      })}
      {unitLabel ? (
        <text x={width / 2} y={height - 4} textAnchor="middle" className="figure-label" fill="var(--ink-faint)">
          {unitLabel}
        </text>
      ) : null}
    </svg>
  );
}

/** A typographic minus sign, not a hyphen. This is a mathematics site. */
const minus = (value: number): string => String(value).replace('-', '\u2212');

function CoordinateGrid({
  min = -5,
  max = 5,
  points = [],
  lines = [],
  showQuadrantLabels = false,
}: {
  min?: number;
  max?: number;
  points?: { x: number; y: number; label?: string }[];
  lines?: { slope: number; intercept: number; label?: string; dashed?: boolean; shade?: 'above' | 'below' }[];
  showQuadrantLabels?: boolean;
}): JSX.Element {
  const size = 300;
  const padding = 18;
  const span = max - min || 1;
  const scale = (size - 2 * padding) / span;
  const px = (x: number): number => padding + (x - min) * scale;
  // SVG y grows downwards; the plane's does not.
  const py = (y: number): number => size - padding - (y - min) * scale;

  const ticks: number[] = [];
  for (let v = min; v <= max; v += 1) ticks.push(v);

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="figure-svg" role="img"
         aria-label={
           [
             ...points.map((p) => `point (${p.x}, ${p.y})`),
             ...lines.map(
               (l) =>
                 `line with slope ${l.slope} through (0, ${l.intercept})` +
                 (l.shade ? `, with the region ${l.shade} it shaded` : ''),
             ),
           ].join('; ') || 'Coordinate grid'
         }>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={px(v)} y1={py(min)} x2={px(v)} y2={py(max)} stroke="var(--rule)" strokeWidth={1} />
          <line x1={px(min)} y1={py(v)} x2={px(max)} y2={py(v)} stroke="var(--rule)" strokeWidth={1} />
        </g>
      ))}

      <line x1={px(min)} y1={py(0)} x2={px(max)} y2={py(0)} stroke="var(--ink-muted)" strokeWidth={2} />
      <line x1={px(0)} y1={py(min)} x2={px(0)} y2={py(max)} stroke="var(--ink-muted)" strokeWidth={2} />

      {ticks
        .filter((v) => v !== 0 && (span <= 12 || v % 2 === 0))
        .map((v) => (
          <g key={`t${v}`}>
            <text x={px(v)} y={py(0) + 14} textAnchor="middle" className="figure-tick" fill="var(--ink-faint)">
              {minus(v)}
            </text>
            <text x={px(0) - 7} y={py(v) + 4} textAnchor="end" className="figure-tick" fill="var(--ink-faint)">
              {minus(v)}
            </text>
          </g>
        ))}

      {showQuadrantLabels
        ? (
            [
              { text: 'I', x: max / 2, y: max / 2 },
              { text: 'II', x: min / 2, y: max / 2 },
              { text: 'III', x: min / 2, y: min / 2 },
              { text: 'IV', x: max / 2, y: min / 2 },
            ] as const
          ).map((q) => (
            <text key={q.text} x={px(q.x)} y={py(q.y)} textAnchor="middle" className="figure-label" fill="var(--ink-faint)">
              {q.text}
            </text>
          ))
        : null}

      {/*
        * Shading is drawn before the axes' lines and before the points, so a
        * boundary is never hidden under the region it bounds.
        */}
      {lines.map((line, index) =>
        line.shade === undefined ? null : (
          <polygon
            key={`shade${index}`}
            points={halfPlane(min, max, line.slope, line.intercept, line.shade)
              .map((corner) => `${px(corner.x)},${py(corner.y)}`)
              .join(' ')}
            fill="var(--accent-soft)"
            stroke="none"
          />
        ),
      )}

      {lines.map((line, index) => {
        /*
         * Clipped to the visible window rather than drawn edge to edge: a steep
         * line leaves the top of the grid long before it reaches the right-hand
         * side, and running it to the corner would misreport where it goes.
         */
        const at = (x: number): number => line.slope * x + line.intercept;
        const within = (y: number): boolean => y >= min && y <= max;
        const ends: { x: number; y: number }[] = [];
        for (const x of [min, max]) if (within(at(x))) ends.push({ x, y: at(x) });
        if (line.slope !== 0) {
          for (const y of [min, max]) {
            const x = (y - line.intercept) / line.slope;
            if (x >= min && x <= max) ends.push({ x, y });
          }
        }
        if (ends.length < 2) return null;
        const [a, b] = [ends[0] as { x: number; y: number }, ends[ends.length - 1] as { x: number; y: number }];

        return (
          <g key={`line${index}`}>
            <line
              x1={px(a.x)} y1={py(a.y)} x2={px(b.x)} y2={py(b.y)}
              stroke="var(--accent)" strokeWidth={2.5}
              strokeDasharray={line.dashed ? '6 5' : undefined}
            />
            {line.label ? (
              <text
                x={px((a.x + b.x) / 2) + 8} y={py((a.y + b.y) / 2) - 8}
                className="figure-label-strong" fill="var(--accent)"
              >
                {line.label}
              </text>
            ) : null}
          </g>
        );
      })}

      {points.map((point, index) => (
        <g key={`${point.x},${point.y},${index}`}>
          <circle cx={px(point.x)} cy={py(point.y)} r={5.5} fill="var(--accent)" />
          {point.label ? (
            <text x={px(point.x) + 9} y={py(point.y) - 8} className="figure-label-strong" fill="var(--accent)">
              {point.label}
            </text>
          ) : null}
        </g>
      ))}
    </svg>
  );
}

/**
 * The corners of the visible half-plane y ≥ mx + c (or ≤), in plane
 * coordinates.
 *
 * Clipping the square against the boundary rather than filling a triangle from
 * the two crossing points: a shallow line crosses the left and right edges and
 * leaves a four-sided region, a steep one crosses the top and bottom and leaves
 * a different four-sided region, and a line missing the window entirely leaves
 * the whole square or none of it. A triangle is right in none of those cases,
 * and a figure that shades the wrong region teaches the wrong region.
 */
function halfPlane(
  min: number,
  max: number,
  slope: number,
  intercept: number,
  side: 'above' | 'below',
): { x: number; y: number }[] {
  const square = [
    { x: min, y: min },
    { x: max, y: min },
    { x: max, y: max },
    { x: min, y: max },
  ];
  // Positive inside the kept half, negative outside, zero on the boundary.
  const test = (p: { x: number; y: number }): number =>
    (side === 'above' ? 1 : -1) * (p.y - (slope * p.x + intercept));

  const out: { x: number; y: number }[] = [];
  for (let i = 0; i < square.length; i += 1) {
    const current = square[i] as { x: number; y: number };
    const next = square[(i + 1) % square.length] as { x: number; y: number };
    const here = test(current);
    const there = test(next);
    if (here >= 0) out.push(current);
    if ((here >= 0) !== (there >= 0)) {
      const t = here / (here - there);
      out.push({ x: current.x + t * (next.x - current.x), y: current.y + t * (next.y - current.y) });
    }
  }
  return out;
}

function Solid({
  solid,
  labels = {},
}: {
  solid: 'cylinder' | 'cone' | 'sphere';
  labels?: Record<string, string>;
}): JSX.Element {
  const width = 240;
  const height = 210;
  const cx = width / 2;
  const rx = 58;
  const ry = 18;

  /* A dashed back edge is what reads as "round" rather than "flat". */
  const ellipse = (cy: number): JSX.Element => (
    <>
      <path
        d={`M ${cx - rx},${cy} A ${rx},${ry} 0 0 0 ${cx + rx},${cy}`}
        fill="none" stroke="var(--accent)" strokeWidth={1.5} strokeDasharray="5 4"
      />
      <path
        d={`M ${cx - rx},${cy} A ${rx},${ry} 0 0 1 ${cx + rx},${cy}`}
        fill="none" stroke="var(--accent)" strokeWidth={2}
      />
    </>
  );

  if (solid === 'sphere') {
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img" aria-label="Sphere">
        <circle cx={cx} cy={height / 2} r={62} fill="var(--accent-soft)" stroke="var(--accent)" strokeWidth={2} />
        {ellipse(height / 2)}
        <line x1={cx} y1={height / 2} x2={cx + 62} y2={height / 2} stroke="var(--accent)" strokeWidth={2} />
        {labels.radius ? (
          <text x={cx + 31} y={height / 2 - 8} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
            {labels.radius}
          </text>
        ) : null}
      </svg>
    );
  }

  const top = 40;
  const base = 168;

  if (solid === 'cone') {
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img" aria-label="Cone">
        <path
          d={`M ${cx},${top} L ${cx + rx},${base} A ${rx},${ry} 0 0 1 ${cx - rx},${base} Z`}
          fill="var(--accent-soft)" stroke="var(--accent)" strokeWidth={2}
        />
        {ellipse(base)}
        <line x1={cx} y1={top} x2={cx} y2={base} stroke="var(--ink-muted)" strokeWidth={1.5} strokeDasharray="4 4" />
        <line x1={cx} y1={base} x2={cx + rx} y2={base} stroke="var(--accent)" strokeWidth={2} />
        {labels.height ? (
          <text x={cx - 8} y={(top + base) / 2} textAnchor="end" className="figure-label" fill="var(--ink-muted)">
            {labels.height}
          </text>
        ) : null}
        {labels.radius ? (
          <text x={cx + rx / 2} y={base + 20} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
            {labels.radius}
          </text>
        ) : null}
      </svg>
    );
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img" aria-label="Cylinder">
      <rect x={cx - rx} y={top} width={rx * 2} height={base - top} fill="var(--accent-soft)" stroke="none" />
      <line x1={cx - rx} y1={top} x2={cx - rx} y2={base} stroke="var(--accent)" strokeWidth={2} />
      <line x1={cx + rx} y1={top} x2={cx + rx} y2={base} stroke="var(--accent)" strokeWidth={2} />
      {ellipse(base)}
      <ellipse cx={cx} cy={top} rx={rx} ry={ry} fill="var(--paper-sunken)" stroke="var(--accent)" strokeWidth={2} />
      <line x1={cx} y1={top} x2={cx + rx} y2={top} stroke="var(--accent)" strokeWidth={2} />
      {labels.radius ? (
        <text x={cx + rx / 2} y={top - 8} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {labels.radius}
        </text>
      ) : null}
      {labels.height ? (
        <text x={cx + rx + 10} y={(top + base) / 2} className="figure-label" fill="var(--ink-muted)">
          {labels.height}
        </text>
      ) : null}
    </svg>
  );
}

function ParallelLines({
  mark,
  labels = {},
}: {
  mark?: [number, number];
  labels?: Record<string, string>;
}): JSX.Element {
  const width = 320;
  const height = 220;
  const topY = 70;
  const bottomY = 155;
  /* The transversal leans, because two perpendicular lines make every angle a
     right angle and the whole point is that the eight angles differ. */
  const lean = 42;
  const midX = width / 2;
  const at = (y: number): number => midX + ((y - (topY + bottomY) / 2) / (bottomY - topY)) * lean * 2;

  /* Angle 1 is above-left of the upper crossing, then clockwise; 5-8 repeat at
     the lower crossing. Positions are offsets from the intersection. */
  const corners: [number, number][] = [
    [-26, -14],
    [26, -14],
    [30, 20],
    [-22, 20],
  ];

  const numbers = [
    ...corners.map(([dx, dy], i) => ({ n: i + 1, x: at(topY) + dx, y: topY + dy })),
    ...corners.map(([dx, dy], i) => ({ n: i + 5, x: at(bottomY) + dx, y: bottomY + dy })),
  ];

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label="Two parallel lines cut by a transversal, with eight numbered angles">
      <line x1={20} y1={topY} x2={width - 20} y2={topY} stroke="var(--accent)" strokeWidth={2} />
      <line x1={20} y1={bottomY} x2={width - 20} y2={bottomY} stroke="var(--accent)" strokeWidth={2} />
      <line x1={at(topY - 45)} y1={topY - 45} x2={at(bottomY + 45)} y2={bottomY + 45}
            stroke="var(--ink-muted)" strokeWidth={2} />

      {/* Arrowheads are what say "parallel" without a caption. */}
      {[topY, bottomY].map((y) => (
        <g key={`arrow${y}`} stroke="var(--accent)" strokeWidth={2} fill="none">
          <polyline points={`${width - 42},${y - 6} ${width - 32},${y} ${width - 42},${y + 6}`} />
        </g>
      ))}

      {numbers.map(({ n, x, y }) => (
        <g key={n}>
          {mark?.includes(n) ? <circle cx={x} cy={y} r={12} fill="var(--accent-soft)" /> : null}
          <text x={x} y={y + 4} textAnchor="middle" className="figure-label"
                fill={mark?.includes(n) ? 'var(--accent)' : 'var(--ink-faint)'}>
            {labels[String(n)] ?? n}
          </text>
        </g>
      ))}
    </svg>
  );
}

function RegularPolygon({
  sides,
  label,
  showCentre = false,
}: {
  sides: number;
  label?: string;
  showCentre?: boolean;
}): JSX.Element {
  const size = 230;
  const centre = size / 2;
  const radius = 82;
  const count = Math.max(3, Math.min(12, Math.round(sides)));

  // Start at the top, so a triangle points up and a square sits square.
  const points = Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * 2 * Math.PI - Math.PI / 2;
    return [centre + radius * Math.cos(angle), centre + radius * Math.sin(angle)] as const;
  });

  return (
    <svg viewBox={`0 0 ${size} ${size + (label ? 22 : 0)}`} className="figure-svg" role="img"
         aria-label={`Regular polygon with ${count} sides`}>
      <polygon points={points.map(([x, y]) => `${x},${y}`).join(' ')}
               fill="var(--accent-soft)" stroke="var(--accent)" strokeWidth={2} />
      {showCentre
        ? points.map(([x, y], i) => (
            <line key={i} x1={centre} y1={centre} x2={x} y2={y}
                  stroke="var(--ink-faint)" strokeWidth={1} strokeDasharray="4 4" />
          ))
        : null}
      {showCentre ? <circle cx={centre} cy={centre} r={3} fill="var(--ink-muted)" /> : null}
      {label ? (
        <text x={centre} y={size + 14} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}

function CircleDiagram({
  elements,
  labels = {},
}: {
  elements: ('radius' | 'diameter' | 'chord' | 'tangent' | 'central-angle' | 'inscribed-angle')[];
  labels?: Record<string, string>;
}): JSX.Element {
  const size = 250;
  const cx = size / 2;
  const cy = size / 2;
  const r = 82;
  const has = (name: string): boolean => elements.includes(name as (typeof elements)[number]);

  /** A point on the circle at the given angle, measured from the top. */
  const on = (degrees: number): [number, number] => {
    const a = ((degrees - 90) * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };

  const [ax, ay] = on(210);
  const [bx, by] = on(330);
  const [px, py] = on(60);
  const [tx, ty] = on(0);

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="figure-svg" role="img"
         aria-label={`Circle showing ${elements.join(', ')}`}>
      <circle cx={cx} cy={cy} r={r} fill="var(--accent-soft)" stroke="var(--accent)" strokeWidth={2} />
      {(has('radius') || has('central-angle')) ? <circle cx={cx} cy={cy} r={3} fill="var(--ink-muted)" /> : null}

      {has('radius') ? (
        <line x1={cx} y1={cy} x2={bx} y2={by} stroke="var(--accent)" strokeWidth={2} />
      ) : null}
      {has('diameter') ? (
        <line x1={ax} y1={ay} x2={bx} y2={by} stroke="var(--accent)" strokeWidth={2} />
      ) : null}
      {has('chord') ? (
        <line x1={ax} y1={ay} x2={bx} y2={by} stroke="var(--ink-muted)" strokeWidth={2} />
      ) : null}
      {has('central-angle') ? (
        <g stroke="var(--accent)" strokeWidth={2} fill="none">
          <line x1={cx} y1={cy} x2={ax} y2={ay} />
          <line x1={cx} y1={cy} x2={bx} y2={by} />
        </g>
      ) : null}
      {has('inscribed-angle') ? (
        <g stroke="var(--ink-muted)" strokeWidth={2} fill="none">
          <line x1={px} y1={py} x2={ax} y2={ay} />
          <line x1={px} y1={py} x2={bx} y2={by} />
          <circle cx={px} cy={py} r={3} fill="var(--ink-muted)" stroke="none" />
        </g>
      ) : null}
      {has('tangent') ? (
        <>
          {/* A tangent meets the radius at a right angle — the small square says so. */}
          <line x1={tx - 70} y1={ty} x2={tx + 70} y2={ty} stroke="var(--ink-muted)" strokeWidth={2} />
          <line x1={cx} y1={cy} x2={tx} y2={ty} stroke="var(--accent)" strokeWidth={2} strokeDasharray="4 4" />
          <polyline points={`${tx + 12},${ty} ${tx + 12},${ty + 12} ${tx},${ty + 12}`}
                    fill="none" stroke="var(--ink-muted)" strokeWidth={1.5} />
        </>
      ) : null}

      {Object.entries(labels).map(([key, value], index) => (
        <text key={key} x={cx} y={size - 8 - index * 16} textAnchor="middle"
              className="figure-label" fill="var(--ink-muted)">
          {value}
        </text>
      ))}
    </svg>
  );
}

function CircleSector({
  degrees,
  radiusLabel,
  arcLabel,
  label,
}: {
  degrees: number;
  radiusLabel?: string;
  arcLabel?: string;
  label?: string;
}): JSX.Element {
  const size = 250;
  const cx = size / 2;
  const cy = size / 2 - 6;
  const r = 80;

  /*
   * Clamped, not rejected — a figure is never the right place to throw. The
   * bounds matter beyond tidiness: a 360° wedge is a path whose two ends
   * coincide, which SVG draws as nothing at all.
   */
  const opening = Math.max(1, Math.min(359, degrees));

  /** A point at `deg` anticlockwise from the positive x-axis. */
  const at = (deg: number, length: number): [number, number] => {
    const a = (deg * Math.PI) / 180;
    return [cx + length * Math.cos(a), cy - length * Math.sin(a)];
  };

  const [startX, startY] = at(0, r);
  const [endX, endY] = at(opening, r);
  const large = opening > 180 ? 1 : 0;
  // Anticlockwise on the page, because SVG's y axis points down.
  const wedge = `M ${cx} ${cy} L ${startX} ${startY} A ${r} ${r} 0 ${large} 0 ${endX} ${endY} Z`;
  const arcOnly = `M ${startX} ${startY} A ${r} ${r} 0 ${large} 0 ${endX} ${endY}`;

  const [angleX, angleY] = at(opening / 2, opening > 40 ? 34 : 48);
  const [arcLabelX, arcLabelY] = at(opening / 2, r + 22);

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="figure-svg" role="img"
         aria-label={`Sector of a circle with an angle of ${Math.round(opening)} degrees`}>
      {/* The rest of the disc, faint: a sector is a fraction of something. */}
      <circle cx={cx} cy={cy} r={r} fill="var(--paper-sunken)" stroke="var(--rule-strong)"
              strokeWidth={1.5} strokeDasharray="3 4" />
      <path d={wedge} fill="var(--accent-soft)" stroke="var(--accent)" strokeWidth={2} />
      {/* Drawn again alone and heavier: the arc is what half the questions ask for. */}
      <path d={arcOnly} fill="none" stroke="var(--accent)" strokeWidth={4} />
      <circle cx={cx} cy={cy} r={3} fill="var(--ink-muted)" />

      <text x={angleX} y={angleY + 4} textAnchor="middle" className="figure-label" fill="var(--ink)">
        {`${Math.round(opening)}°`}
      </text>
      {radiusLabel ? (
        <text x={cx + r / 2} y={cy + 18} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {radiusLabel}
        </text>
      ) : null}
      {arcLabel ? (
        <text x={arcLabelX} y={arcLabelY} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {arcLabel}
        </text>
      ) : null}
      {label ? (
        <text x={cx} y={size - 4} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}

function ConstructionFigure({
  construction,
  showArcs = true,
  label,
}: {
  construction: 'perpendicular-bisector' | 'angle-bisector' | 'equilateral-triangle';
  showArcs?: boolean;
  label?: string;
}): JSX.Element {
  const width = 300;
  const height = 214;

  /** A point at `deg` anticlockwise from the positive x-axis, about a centre. */
  const from = (ox: number, oy: number, deg: number, length: number): [number, number] => {
    const a = (deg * Math.PI) / 180;
    return [ox + length * Math.cos(a), oy - length * Math.sin(a)];
  };

  /*
   * An arc of the circle a compass would have swung, given as an angle range
   * rather than as endpoints. Every one of these figures is defined by "put
   * the point here, open it this wide", and deriving the coordinates from that
   * keeps the picture honest when a radius is adjusted.
   */
  const arc = (ox: number, oy: number, radius: number, fromDeg: number, toDeg: number): string => {
    const [x1, y1] = from(ox, oy, fromDeg, radius);
    const [x2, y2] = from(ox, oy, toDeg, radius);
    const large = Math.abs(toDeg - fromDeg) > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} ${toDeg > fromDeg ? 0 : 1} ${x2} ${y2}`;
  };

  const arcStroke = { fill: 'none', stroke: 'var(--ink-muted)', strokeWidth: 1.5, strokeDasharray: '4 3' } as const;
  const pointStyle = { r: 3.5, fill: 'var(--accent)' } as const;

  const drawing = ((): { aria: string; body: JSX.Element } => {
    if (construction === 'angle-bisector') {
      const vx = 58;
      const vy = 176;
      const opening = 56;
      const reach = 92; // where the first arc cuts the two arms
      const span = 74; // the second opening, swung from each cut
      const [px, py] = from(vx, vy, 0, reach);
      const [qx, qy] = from(vx, vy, opening, reach);
      const half = opening / 2;
      // Where the two equal arcs meet — on the bisector, by symmetry.
      const along = reach * Math.cos((half * Math.PI) / 180);
      const off = reach * Math.sin((half * Math.PI) / 180);
      const meet = along + Math.sqrt(Math.max(1, span * span - off * off));
      const [mx, my] = from(vx, vy, half, meet);
      const [endX, endY] = from(vx, vy, half, meet + 32);
      const [arm1X, arm1Y] = from(vx, vy, 0, 220);
      const [arm2X, arm2Y] = from(vx, vy, opening, 196);
      /*
       * Each of the two arcs is swept around the direction of the point it
       * produces, rather than through a hand-picked range. Fixed ranges look
       * plausible and are the wrong picture: an arc drawn near the crossing
       * but not through it says the construction merely comes close, which is
       * the one thing the lesson is arguing against.
       */
      const bearing = (ox: number, oy: number): number => (Math.atan2(oy - my, mx - ox) * 180) / Math.PI;

      return {
        aria: 'Bisecting an angle with compass arcs swung from two points equidistant from the vertex',
        body: (
          <>
            <g stroke="var(--rule-strong)" strokeWidth={2} fill="none">
              <line x1={vx} y1={vy} x2={arm1X} y2={arm1Y} />
              <line x1={vx} y1={vy} x2={arm2X} y2={arm2Y} />
            </g>
            {showArcs ? (
              <g {...arcStroke}>
                <path d={arc(vx, vy, reach, -8, opening + 8)} />
                <path d={arc(px, py, span, bearing(px, py) - 32, bearing(px, py) + 32)} />
                <path d={arc(qx, qy, span, bearing(qx, qy) - 32, bearing(qx, qy) + 32)} />
              </g>
            ) : null}
            <line x1={vx} y1={vy} x2={endX} y2={endY} stroke="var(--accent)" strokeWidth={2} />
            {showArcs ? (
              <>
                <circle cx={px} cy={py} {...pointStyle} />
                <circle cx={qx} cy={qy} {...pointStyle} />
              </>
            ) : null}
            <circle cx={mx} cy={my} {...pointStyle} />
            <text x={vx - 14} y={vy + 6} className="figure-label" fill="var(--ink-muted)">A</text>
          </>
        ),
      };
    }

    if (construction === 'equilateral-triangle') {
      const ax = 88;
      const ay = 178;
      const side = 124;
      const bx = ax + side;
      const by = ay;
      const [px, py] = from(ax, ay, 60, side);

      return {
        aria: 'Constructing an equilateral triangle from two arcs of the same radius as the segment',
        body: (
          <>
            {showArcs ? (
              <g {...arcStroke}>
                <path d={arc(ax, ay, side, 38, 82)} />
                <path d={arc(bx, by, side, 98, 142)} />
              </g>
            ) : null}
            <g stroke="var(--accent)" strokeWidth={2} fill="none">
              <line x1={ax} y1={ay} x2={bx} y2={by} />
              <line x1={ax} y1={ay} x2={px} y2={py} />
              <line x1={bx} y1={by} x2={px} y2={py} />
            </g>
            <circle cx={ax} cy={ay} {...pointStyle} />
            <circle cx={bx} cy={by} {...pointStyle} />
            <circle cx={px} cy={py} {...pointStyle} />
            <text x={ax - 16} y={ay + 6} className="figure-label" fill="var(--ink-muted)">A</text>
            <text x={bx + 8} y={by + 6} className="figure-label" fill="var(--ink-muted)">B</text>
            <text x={px} y={py - 10} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">C</text>
          </>
        ),
      };
    }

    // The perpendicular bisector — the one whose radius carries a condition.
    const ax = 66;
    const ay = 112;
    const bx = 234;
    const by = 112;
    const half = (bx - ax) / 2;
    const radius = half + 26;
    const rise = Math.sqrt(radius * radius - half * half);
    const midX = (ax + bx) / 2;

    return {
      aria: 'Constructing the perpendicular bisector of a segment from two equal arcs',
      body: (
        <>
          {showArcs ? (
            <g {...arcStroke}>
              <path d={arc(ax, ay, radius, -52, 52)} />
              <path d={arc(bx, by, radius, 128, 232)} />
            </g>
          ) : null}
          <line x1={ax} y1={ay} x2={bx} y2={by} stroke="var(--rule-strong)" strokeWidth={2} />
          <line x1={midX} y1={ay - rise - 14} x2={midX} y2={ay + rise + 14} stroke="var(--accent)" strokeWidth={2} />
          <polyline points={`${midX + 12},${ay} ${midX + 12},${ay - 12} ${midX},${ay - 12}`}
                    fill="none" stroke="var(--ink-muted)" strokeWidth={1.5} />
          <circle cx={ax} cy={ay} {...pointStyle} />
          <circle cx={bx} cy={by} {...pointStyle} />
          {showArcs ? (
            <>
              <circle cx={midX} cy={ay - rise} {...pointStyle} />
              <circle cx={midX} cy={ay + rise} {...pointStyle} />
            </>
          ) : null}
          <text x={ax - 16} y={ay + 6} className="figure-label" fill="var(--ink-muted)">A</text>
          <text x={bx + 8} y={by + 6} className="figure-label" fill="var(--ink-muted)">B</text>
        </>
      ),
    };
  })();

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img" aria-label={drawing.aria}>
      {drawing.body}
      {label ? (
        <text x={width / 2} y={height - 4} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}

function DotPlot({
  values,
  from,
  to,
  label,
}: {
  values: number[];
  from?: number;
  to?: number;
  label?: string;
}): JSX.Element {
  const low = from ?? Math.min(...values);
  const high = to ?? Math.max(...values);
  const width = 340;
  const padding = 24;
  const dot = 9;
  const span = high - low || 1;
  const px = (v: number): number => padding + ((v - low) / span) * (width - 2 * padding);

  const counts = new Map<number, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  const tallest = Math.max(...counts.values(), 1);
  const height = tallest * dot + 54 + (label ? 18 : 0);
  const baseline = tallest * dot + 24;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label={`Dot plot of ${values.length} values from ${low} to ${high}`}>
      <line x1={padding - 8} y1={baseline} x2={width - padding + 8} y2={baseline} stroke="var(--rule-strong)" strokeWidth={2} />
      {Array.from({ length: high - low + 1 }, (_, i) => low + i).map((value) => (
        <g key={value}>
          <line x1={px(value)} y1={baseline - 4} x2={px(value)} y2={baseline + 4} stroke="var(--rule-strong)" strokeWidth={1.5} />
          <text x={px(value)} y={baseline + 20} textAnchor="middle" className="figure-tick" fill="var(--ink-faint)">
            {value}
          </text>
          {Array.from({ length: counts.get(value) ?? 0 }, (_, stackIndex) => (
            <circle key={stackIndex} cx={px(value)} cy={baseline - 10 - stackIndex * dot} r={dot / 2 - 0.5} fill="var(--accent)" />
          ))}
        </g>
      ))}
      {label ? (
        <text x={width / 2} y={height - 4} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}

function BarChart({ bars, unitLabel }: { bars: { label: string; value: number }[]; unitLabel?: string }): JSX.Element {
  const barWidth = 46;
  const gap = 18;
  const plotHeight = 130;
  const width = bars.length * (barWidth + gap) + gap;
  const height = plotHeight + 52;
  const tallest = Math.max(...bars.map((bar) => bar.value), 1);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label={bars.map((bar) => `${bar.label}: ${bar.value}`).join('; ')}>
      <line x1={gap - 6} y1={plotHeight + 10} x2={width - gap + 6} y2={plotHeight + 10} stroke="var(--rule-strong)" strokeWidth={2} />
      {bars.map((bar, index) => {
        const barHeight = (bar.value / tallest) * plotHeight;
        const x = gap + index * (barWidth + gap);
        return (
          <g key={bar.label}>
            <rect x={x} y={plotHeight + 10 - barHeight} width={barWidth} height={barHeight} rx={3}
                  fill="var(--accent)" stroke="var(--rule-strong)" strokeWidth={1} />
            <text x={x + barWidth / 2} y={plotHeight + 4 - barHeight} textAnchor="middle" className="figure-tick" fill="var(--ink-muted)">
              {bar.value}
            </text>
            <text x={x + barWidth / 2} y={plotHeight + 28} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
              {bar.label}
            </text>
          </g>
        );
      })}
      {unitLabel ? (
        <text x={width / 2} y={height - 4} textAnchor="middle" className="figure-tick" fill="var(--ink-faint)">
          {unitLabel}
        </text>
      ) : null}
    </svg>
  );
}

/**
 * A histogram: touching bars over intervals.
 *
 * Drawn separately from `BarChart` rather than as a flag on it, because the
 * gap between bars is not styling. A bar chart's categories could be shuffled
 * without losing information; a histogram's bins could not, and the bars are
 * pushed together to say exactly that.
 */
function Histogram({ bins, unitLabel }: { bins: { label: string; count: number }[]; unitLabel?: string }): JSX.Element {
  const binWidth = 54;
  const margin = 20;
  const plotHeight = 130;
  const width = bins.length * binWidth + margin * 2;
  const height = plotHeight + 56;
  const tallest = Math.max(...bins.map((bin) => bin.count), 1);
  const baseline = plotHeight + 10;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label={`Histogram: ${bins.map((bin) => `${bin.label}: ${bin.count}`).join('; ')}`}>
      <line x1={margin} y1={baseline} x2={width - margin} y2={baseline} stroke="var(--rule-strong)" strokeWidth={2} />
      {bins.map((bin, index) => {
        const barHeight = (bin.count / tallest) * plotHeight;
        const x = margin + index * binWidth;
        return (
          <g key={bin.label}>
            <rect x={x} y={baseline - barHeight} width={binWidth} height={barHeight}
                  fill="var(--accent-soft)" stroke="var(--rule-strong)" strokeWidth={1.5} />
            <text x={x + binWidth / 2} y={baseline - barHeight - 6} textAnchor="middle" className="figure-tick" fill="var(--ink-muted)">
              {bin.count}
            </text>
            <text x={x + binWidth / 2} y={baseline + 18} textAnchor="middle" className="figure-label" fill="var(--ink-faint)">
              {bin.label}
            </text>
          </g>
        );
      })}
      {unitLabel ? (
        <text x={width / 2} y={height - 6} textAnchor="middle" className="figure-tick" fill="var(--ink-faint)">
          {unitLabel}
        </text>
      ) : null}
    </svg>
  );
}

/**
 * A box plot of the five-number summary.
 *
 * The box holds the middle half of the data and the whiskers hold the rest, so
 * a wide box and short whiskers mean something quite different from the
 * reverse. Both are drawn to scale against a shared axis, because a box plot
 * read without its axis says nothing at all.
 */
function BoxPlot({
  min,
  q1,
  median,
  q3,
  max,
  from,
  to,
  label,
}: {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  from?: number;
  to?: number;
  label?: string;
}): JSX.Element {
  const width = 340;
  const padding = 26;
  const low = from ?? min;
  const high = to ?? max;
  const span = high - low || 1;
  const px = (value: number): number => padding + ((value - low) / span) * (width - 2 * padding);

  const axis = 96;
  const centre = 46;
  const boxHeight = 42;
  const height = axis + 26 + (label ? 18 : 0);
  const top = centre - boxHeight / 2;

  const ticks: number[] = [];
  const step = Math.max(1, Math.ceil(span / 8));
  for (let value = Math.ceil(low / step) * step; value <= high; value += step) ticks.push(value);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label={`Box plot with minimum ${min}, lower quartile ${q1}, median ${median}, upper quartile ${q3} and maximum ${max}`}>
      {/* Whiskers: the outer quarters, which hold half the data between them. */}
      <line x1={px(min)} y1={centre} x2={px(q1)} y2={centre} stroke="var(--rule-strong)" strokeWidth={2} />
      <line x1={px(q3)} y1={centre} x2={px(max)} y2={centre} stroke="var(--rule-strong)" strokeWidth={2} />
      <line x1={px(min)} y1={top} x2={px(min)} y2={top + boxHeight} stroke="var(--rule-strong)" strokeWidth={2} />
      <line x1={px(max)} y1={top} x2={px(max)} y2={top + boxHeight} stroke="var(--rule-strong)" strokeWidth={2} />

      <rect x={px(q1)} y={top} width={Math.max(2, px(q3) - px(q1))} height={boxHeight}
            fill="var(--accent-soft)" stroke="var(--rule-strong)" strokeWidth={2} />
      <line x1={px(median)} y1={top} x2={px(median)} y2={top + boxHeight} stroke="var(--accent)" strokeWidth={3} />

      <line x1={padding - 8} y1={axis} x2={width - padding + 8} y2={axis} stroke="var(--rule)" strokeWidth={1.5} />
      {ticks.map((tick) => (
        <g key={tick}>
          <line x1={px(tick)} y1={axis - 4} x2={px(tick)} y2={axis + 4} stroke="var(--rule)" strokeWidth={1.5} />
          <text x={px(tick)} y={axis + 18} textAnchor="middle" className="figure-tick" fill="var(--ink-faint)">
            {minus(tick)}
          </text>
        </g>
      ))}
      {label ? (
        <text x={width / 2} y={height - 3} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}

/** A rectangular prism in oblique projection — enough depth to read as a solid. */
function Prism({
  length,
  width: depth,
  height,
  labels = {},
}: {
  length: number;
  width: number;
  height: number;
  labels?: Record<string, string>;
}): JSX.Element {
  const scale = 150 / Math.max(length, height, 1);
  const w = Math.max(60, length * scale);
  const h = Math.max(45, height * scale);
  const d = Math.max(24, Math.min(46, depth * scale * 0.55));
  const pad = 34;
  const svgWidth = w + d + pad * 2;
  const svgHeight = h + d + pad * 2;
  const x0 = pad;
  const y0 = pad + d;

  return (
    <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="figure-svg" role="img"
         aria-label={`Rectangular prism, ${length} by ${depth} by ${height}`}>
      {/* Top and right faces first, so the front face overlaps them cleanly. */}
      <polygon points={`${x0},${y0} ${x0 + d},${y0 - d} ${x0 + w + d},${y0 - d} ${x0 + w},${y0}`}
               fill="var(--accent-soft)" stroke="var(--accent)" strokeWidth={1.5} />
      <polygon points={`${x0 + w},${y0} ${x0 + w + d},${y0 - d} ${x0 + w + d},${y0 + h - d} ${x0 + w},${y0 + h}`}
               fill="var(--accent-soft)" stroke="var(--accent)" strokeWidth={1.5} />
      <rect x={x0} y={y0} width={w} height={h} fill="var(--paper-sunken)" stroke="var(--accent)" strokeWidth={2} />

      {labels.length ? (
        <text x={x0 + w / 2} y={y0 + h + 20} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {labels.length}
        </text>
      ) : null}
      {labels.height ? (
        <text x={x0 - 8} y={y0 + h / 2 + 4} textAnchor="end" className="figure-label" fill="var(--ink-muted)">
          {labels.height}
        </text>
      ) : null}
      {labels.width ? (
        <text x={x0 + w + d + 6} y={y0 - d / 2 + 4} className="figure-label" fill="var(--ink-muted)">
          {labels.width}
        </text>
      ) : null}
    </svg>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * One angle, drawn as two rays from a vertex, optionally under a protractor.
 *
 * The fixed ray points right and the second opens anticlockwise, which is how
 * every protractor in a classroom is used. Both scales are printed when the
 * protractor is shown: reading 130 where the answer is 50 is *the* Grade 4
 * angle error, and it cannot be diagnosed from a picture that quietly hides the
 * scale the student misread.
 */
function AngleFigure({
  degrees,
  protractor = false,
  split,
  label,
  labels = {},
}: {
  degrees: number;
  protractor?: boolean;
  split?: number;
  label?: string;
  labels?: Record<string, string>;
}): JSX.Element {
  const width = 320;
  const height = protractor ? 226 : 180;
  const cx = width / 2;
  const cy = height - 40;
  const armLength = protractor ? 128 : 132;
  const dial = 104;
  /*
   * Both scales sit *inside* the rim, one row above the other, as they do on a
   * real protractor — and 22px apart, which is enough that "150" on one row and
   * "30" on the other do not touch where they meet at the base.
   */
  const directScale = dial - 18;
  const reverseScale = dial - 40;

  // Clamped rather than rejected: a figure is never the right place to throw.
  const opening = Math.max(0, Math.min(180, degrees));
  const interior = split === undefined ? undefined : Math.max(0, Math.min(opening, split));

  /** A point at `deg` degrees anticlockwise from the right-hand ray. */
  const at = (deg: number, length: number): [number, number] => {
    const a = (deg * Math.PI) / 180;
    return [cx + length * Math.cos(a), cy - length * Math.sin(a)];
  };

  const [ax, ay] = at(0, armLength);
  const [bx, by] = at(opening, armLength);
  const arcRadius = protractor ? 34 : 42;
  const [arcStartX, arcStartY] = at(0, arcRadius);
  const [arcEndX, arcEndY] = at(opening, arcRadius);
  /*
   * Under a protractor the bisector is crowded with scale numbers, so the
   * angle's own label goes outside the rim instead — still between the two
   * arms, so it is unmistakably labelling this angle and not the reflex one.
   */
  const [markX, markY] = at(opening / 2, protractor ? dial + 16 : arcRadius + 22);
  const [splitX, splitY] = interior === undefined ? [0, 0] : at(interior, armLength);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label={`Angle of ${Math.round(opening)} degrees${protractor ? ', shown on a protractor' : ''}`}>
      {protractor ? (
        <>
          <path d={`M ${cx - dial} ${cy} A ${dial} ${dial} 0 0 1 ${cx + dial} ${cy} Z`}
                fill="var(--paper-sunken)" stroke="var(--rule-strong)" strokeWidth={1.5} />
          {Array.from({ length: 19 }, (_, i) => {
            const deg = i * 10;
            const [x1, y1] = at(deg, dial - 9);
            const [x2, y2] = at(deg, dial);
            const [tx, ty] = at(deg, directScale);
            return (
              <g key={deg}>
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--rule-strong)" strokeWidth={1} />
                {deg % 30 === 0 ? (
                  <text x={tx} y={ty + 3} textAnchor="middle" className="figure-label" fill="var(--ink-muted)"
                        fontSize={10}>
                    {deg}
                  </text>
                ) : null}
              </g>
            );
          })}
          {/* The second scale, which runs the other way — and catches most students. */}
          {Array.from({ length: 7 }, (_, i) => {
            const deg = i * 30;
            const [tx, ty] = at(deg, reverseScale);
            return (
              <text key={`reverse-${deg}`} x={tx} y={ty + 3} textAnchor="middle" className="figure-label"
                    fill="var(--ink-faint)" fontSize={10}>
                {180 - deg}
              </text>
            );
          })}
        </>
      ) : null}

      <path d={`M ${arcStartX} ${arcStartY} A ${arcRadius} ${arcRadius} 0 0 0 ${arcEndX} ${arcEndY}`}
            fill="none" stroke="var(--accent)" strokeWidth={2} />
      <line x1={cx} y1={cy} x2={ax} y2={ay} stroke="var(--ink)" strokeWidth={2.5} strokeLinecap="round" />
      <line x1={cx} y1={cy} x2={bx} y2={by} stroke="var(--ink)" strokeWidth={2.5} strokeLinecap="round" />
      {interior === undefined ? null : (
        <line x1={cx} y1={cy} x2={splitX} y2={splitY}
              stroke="var(--accent)" strokeWidth={2.5} strokeLinecap="round" strokeDasharray="6 4" />
      )}
      <circle cx={cx} cy={cy} r={4} fill="var(--ink)" />

      {labels.angle ? (
        <text x={markX} y={markY} textAnchor="middle" className="figure-label" fill="var(--ink)">
          {labels.angle}
        </text>
      ) : null}
      {labels.vertex ? (
        <text x={cx - 14} y={cy + 17} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {labels.vertex}
        </text>
      ) : null}
      {label ? (
        <text x={cx} y={height - 6} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}
/**
 * Where a mirror line ends: at a vertex, or at the midpoint of an edge.
 *
 * Storing the *anchors* rather than coordinates is what keeps this data
 * honest. Hand-written endpoints for a pentagon's five axes look plausible and
 * are almost never right, and a diagram that draws five lines through roughly
 * the middle teaches a student that "roughly the middle" is what symmetry
 * means.
 */
type MirrorAnchor = { vertex: number } | { edge: number };

type ShapeSpec = { points: [number, number][]; mirrors: [MirrorAnchor, MirrorAnchor][]; name: string };

/**
 * A regular polygon on the unit square, with every one of its axes.
 *
 * An odd polygon has one axis per vertex, each running to the midpoint of the
 * opposite edge. An even one has half its axes through opposite vertices and
 * half through opposite edge midpoints — still one per side, but arrived at
 * two different ways, which is the fact the lesson is built on.
 */
function regular(sides: number, rotationDegrees = 0): Omit<ShapeSpec, 'name'> {
  const radius = 0.45;
  const points = Array.from({ length: sides }, (_, i) => {
    const angle = (i / sides) * 2 * Math.PI - Math.PI / 2 + (rotationDegrees * Math.PI) / 180;
    return [0.5 + radius * Math.cos(angle), 0.5 + radius * Math.sin(angle)] as [number, number];
  });

  const mirrors: [MirrorAnchor, MirrorAnchor][] = [];
  if (sides % 2 === 1) {
    for (let i = 0; i < sides; i += 1) mirrors.push([{ vertex: i }, { edge: (i + (sides - 1) / 2) % sides }]);
  } else {
    for (let i = 0; i < sides / 2; i += 1) mirrors.push([{ vertex: i }, { vertex: i + sides / 2 }]);
    for (let i = 0; i < sides / 2; i += 1) mirrors.push([{ edge: i }, { edge: i + sides / 2 }]);
  }
  return { points, mirrors };
}

/**
 * The shapes symmetry is taught with.
 *
 * The irregular ones are written out, because they are the point: a rectangle
 * has two axes and a parallelogram has none, however many sides they share
 * with the square beside them.
 */
const SYMMETRY_SHAPES: Record<string, ShapeSpec> = {
  square: { ...regular(4, 45), name: 'Square' },
  'equilateral-triangle': { ...regular(3), name: 'Equilateral triangle' },
  'regular-pentagon': { ...regular(5), name: 'Regular pentagon' },
  'regular-hexagon': { ...regular(6, 30), name: 'Regular hexagon' },
  rectangle: {
    points: [[0.04, 0.28], [0.96, 0.28], [0.96, 0.72], [0.04, 0.72]],
    mirrors: [
      [{ edge: 0 }, { edge: 2 }],
      [{ edge: 1 }, { edge: 3 }],
    ],
    name: 'Rectangle',
  },
  'isosceles-triangle': {
    points: [[0.5, 0.08], [0.84, 0.88], [0.16, 0.88]],
    mirrors: [[{ vertex: 0 }, { edge: 1 }]],
    name: 'Isosceles triangle',
  },
  'scalene-triangle': {
    points: [[0.26, 0.1], [0.94, 0.88], [0.06, 0.88]],
    mirrors: [],
    name: 'Scalene triangle',
  },
  parallelogram: {
    points: [[0.3, 0.22], [0.98, 0.22], [0.7, 0.78], [0.02, 0.78]],
    mirrors: [],
    name: 'Parallelogram',
  },
  /* Deliberately far from a rhombus: equal diagonals would give it two axes. */
  kite: {
    points: [[0.5, 0.06], [0.85, 0.32], [0.5, 0.94], [0.15, 0.32]],
    mirrors: [[{ vertex: 0 }, { vertex: 2 }]],
    name: 'Kite',
  },
};

/** A named plane shape, with its mirror lines drawn only when asked for. */
function SymmetryFigure({
  shape,
  showLines = false,
  label,
}: {
  shape: string;
  showLines?: boolean;
  label?: string;
}): JSX.Element {
  const size = 210;
  // The fallback is a literal defined just above, so it is never missing; the
  // index signature is simply unable to say so.
  const spec = (SYMMETRY_SHAPES[shape] ?? SYMMETRY_SHAPES.square) as ShapeSpec;
  const height = size + (label ? 20 : 0);
  const { points } = spec;

  const centre: [number, number] = [
    points.reduce((sum, [x]) => sum + x, 0) / points.length,
    points.reduce((sum, [, y]) => sum + y, 0) / points.length,
  ];

  const at = (anchor: MirrorAnchor): [number, number] => {
    if ('vertex' in anchor) return points[anchor.vertex % points.length] as [number, number];
    const [x1, y1] = points[anchor.edge % points.length] as [number, number];
    const [x2, y2] = points[(anchor.edge + 1) % points.length] as [number, number];
    return [(x1 + x2) / 2, (y1 + y2) / 2];
  };

  // Push each end a little past the outline, so a mirror line reads as a fold
  // through the shape rather than as an edge of it.
  const overshoot = ([x, y]: [number, number]): [number, number] => [
    centre[0] + (x - centre[0]) * 1.1,
    centre[1] + (y - centre[1]) * 1.1,
  ];

  return (
    <svg viewBox={`0 0 ${size} ${height}`} className="figure-svg" role="img"
         aria-label={showLines ? `${spec.name} with its lines of symmetry` : spec.name}>
      <polygon points={points.map(([x, y]) => `${x * size},${y * size}`).join(' ')}
               fill="var(--accent-soft)" stroke="var(--accent)" strokeWidth={2} />
      {showLines
        ? spec.mirrors.map(([from, until], i) => {
            const [x1, y1] = overshoot(at(from));
            const [x2, y2] = overshoot(at(until));
            return (
              <line key={i} x1={x1 * size} y1={y1 * size} x2={x2 * size} y2={y2 * size}
                    stroke="var(--ink-muted)" strokeWidth={1.5} strokeDasharray="6 4" />
            );
          })
        : null}
      {label ? (
        <text x={size / 2} y={height - 4} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}

/**
 * Exact coordinates for the angles a student is expected to know by heart.
 *
 * Rounded decimals would defeat the diagram's purpose. The whole claim of the
 * unit circle is that these values are *exact*, and 0.87 does not look like
 * anything worth remembering.
 */
const EXACT_ON_CIRCLE: Record<number, [string, string]> = {
  0: ['1', '0'],
  30: ['√3/2', '1/2'],
  45: ['√2/2', '√2/2'],
  60: ['1/2', '√3/2'],
  90: ['0', '1'],
  120: ['−1/2', '√3/2'],
  135: ['−√2/2', '√2/2'],
  150: ['−√3/2', '1/2'],
  180: ['−1', '0'],
  210: ['−√3/2', '−1/2'],
  225: ['−√2/2', '−√2/2'],
  240: ['−1/2', '−√3/2'],
  270: ['0', '−1'],
  300: ['1/2', '−√3/2'],
  315: ['√2/2', '−√2/2'],
  330: ['√3/2', '−1/2'],
};

function UnitCircle({
  angle,
  showCoordinates = false,
  showQuadrantLabels = false,
  marks = [],
  label,
}: {
  angle?: number;
  showCoordinates?: boolean;
  showQuadrantLabels?: boolean;
  marks?: number[];
  label?: string;
}): JSX.Element {
  const size = 300;
  const c = size / 2;
  const r = 106;
  /*
   * Angles arrive in degrees, because that is how the questions read. The two
   * minus signs are separate: one turns SVG's downward y into the plane's
   * upward y, the other is the usual radian conversion.
   */
  const at = (degrees: number): [number, number] => {
    const a = (degrees * Math.PI) / 180;
    return [c + r * Math.cos(a), c - r * Math.sin(a)];
  };

  const turned = angle === undefined ? undefined : ((angle % 360) + 360) % 360;
  const coordinate =
    turned === undefined
      ? undefined
      : (EXACT_ON_CIRCLE[turned] ?? [
          minus(Math.round(Math.cos((turned * Math.PI) / 180) * 100) / 100),
          minus(Math.round(Math.sin((turned * Math.PI) / 180) * 100) / 100),
        ]);

  const [tipX, tipY] = at(angle ?? 0);
  const arcR = 34;
  const arcEnd = ((degrees: number): [number, number] => {
    const a = (degrees * Math.PI) / 180;
    return [c + arcR * Math.cos(a), c - arcR * Math.sin(a)];
  })(angle ?? 0);

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="figure-svg" role="img"
         aria-label={
           angle === undefined
             ? 'The unit circle'
             : `The unit circle with an angle of ${angle} degrees, meeting the circle at (${coordinate?.[0]}, ${coordinate?.[1]})`
         }>
      <line x1={c - r - 22} y1={c} x2={c + r + 22} y2={c} stroke="var(--ink-muted)" strokeWidth={2} />
      <line x1={c} y1={c + r + 22} x2={c} y2={c - r - 22} stroke="var(--ink-muted)" strokeWidth={2} />
      <circle cx={c} cy={c} r={r} fill="none" stroke="var(--accent)" strokeWidth={2} />

      {showQuadrantLabels
        ? ([['I', 1, 1], ['II', -1, 1], ['III', -1, -1], ['IV', 1, -1]] as const).map(([name, sx, sy]) => (
            <text key={name} x={c + sx * r * 0.62} y={c - sy * r * 0.62 + 5}
                  textAnchor="middle" className="figure-label" fill="var(--ink-faint)">
              {name}
            </text>
          ))
        : null}

      {marks.map((degrees) => {
        const [mx, my] = at(degrees);
        return <circle key={`m${degrees}`} cx={mx} cy={my} r={3.5} fill="var(--ink-faint)" />;
      })}

      {angle === undefined ? null : (
        <g>
          {/* The sweep is drawn as an arc, so which way the angle turns is visible. */}
          <path
            d={`M ${c + arcR} ${c} A ${arcR} ${arcR} 0 ${Math.abs(angle) > 180 ? 1 : 0} 0 ${arcEnd[0]} ${arcEnd[1]}`}
            fill="none" stroke="var(--ink-faint)" strokeWidth={2}
          />
          <line x1={c} y1={c} x2={tipX} y2={tipY} stroke="var(--accent)" strokeWidth={2.5} />
          <circle cx={tipX} cy={tipY} r={5.5} fill="var(--accent)" />
          <text x={c + arcR + 6} y={c - 10} className="figure-label" fill="var(--ink-muted)">
            {`${minus(angle)}°`}
          </text>

          {showCoordinates ? (
            <g>
              {/* Across for cosine, up for sine — the point the diagram exists to make. */}
              <line x1={tipX} y1={tipY} x2={tipX} y2={c} stroke="var(--ink-faint)" strokeWidth={1.5} strokeDasharray="4 4" />
              <line x1={tipX} y1={tipY} x2={c} y2={tipY} stroke="var(--ink-faint)" strokeWidth={1.5} strokeDasharray="4 4" />
              <text x={tipX + (tipX >= c ? 10 : -10)} y={tipY - 10}
                    textAnchor={tipX >= c ? 'start' : 'end'} className="figure-label-strong" fill="var(--accent)">
                {`(${coordinate?.[0]}, ${coordinate?.[1]})`}
              </text>
            </g>
          ) : null}
        </g>
      )}

      {label ? (
        <text x={c} y={size - 4} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}

/** The parent curves. Everything else in Algebra II is one of these, moved. */
const PARENT_CURVES: Record<string, (x: number) => number> = {
  linear: (x) => x,
  quadratic: (x) => x * x,
  cubic: (x) => x ** 3,
  quartic: (x) => x ** 4,
  reciprocal: (x) => 1 / x,
  exponential: (x) => 2 ** x,
  logarithm: (x) => (x > 0 ? Math.log2(x) : Number.NaN),
  sqrt: (x) => (x >= 0 ? Math.sqrt(x) : Number.NaN),
  sine: (x) => Math.sin(x),
  cosine: (x) => Math.cos(x),
  tangent: (x) => Math.tan(x),
  absolute: (x) => Math.abs(x),
};

function FunctionGraph({
  curve,
  roots,
  transform = {},
  asymptotes = {},
  lines = [],
  showParent = false,
  points = [],
  shade,
  min = -5,
  max = 5,
  label,
}: {
  curve?: string;
  roots?: number[];
  transform?: { a?: number; b?: number; h?: number; k?: number };
  asymptotes?: { vertical?: number[]; horizontal?: number[] };
  lines?: { slope: number; intercept: number; label?: string; dashed?: boolean }[];
  showParent?: boolean;
  points?: { x: number; y: number; label?: string }[];
  shade?: { from: number; to: number; rectangles?: number };
  min?: number;
  max?: number;
  label?: string;
}): JSX.Element {
  const size = 300;
  const padding = 18;
  const span = max - min || 1;
  const scale = (size - 2 * padding) / span;
  const px = (x: number): number => padding + (x - min) * scale;
  const py = (y: number): number => size - padding - (y - min) * scale;

  const parent: (x: number) => number = roots
    ? (x) => roots.reduce((product, root) => product * (x - root), 1)
    : (PARENT_CURVES[curve ?? 'linear'] ?? (PARENT_CURVES.linear as (x: number) => number));
  const { a = 1, b = 1, h = 0, k = 0 } = transform;
  const drawn = (x: number): number => a * parent(b * (x - h)) + k;

  /*
   * Sampled into runs rather than one polyline. A curve that leaves the window
   * — or is undefined, as 1/x is at zero and log x is to the left of it — must
   * not be joined across the gap, or the picture grows a vertical line exactly
   * where the function has none. That line is indistinguishable from an
   * asymptote to a student, and it is not one.
   */
  const runs = (f: (x: number) => number): string[] => {
    const paths: string[] = [];
    let current: string[] = [];
    const steps = 240;
    for (let i = 0; i <= steps; i += 1) {
      const x = min + (span * i) / steps;
      const y = f(x);
      if (!Number.isFinite(y) || y < min || y > max) {
        if (current.length > 1) paths.push(current.join(' '));
        current = [];
        continue;
      }
      current.push(`${px(x).toFixed(1)},${py(y).toFixed(1)}`);
    }
    if (current.length > 1) paths.push(current.join(' '));
    return paths;
  };

  const ticks: number[] = [];
  for (let v = Math.ceil(min); v <= max; v += 1) ticks.push(v);

  /*
   * The shaded region, as one polygon following the curve. Clamped to the
   * window so a curve leaving the top of the picture shades a band rather than
   * a spike off the canvas.
   */
  const clamp = (y: number): number => Math.min(max, Math.max(min, y));
  const region = ((): string | null => {
    if (!shade || shade.rectangles) return null;
    const steps = 120;
    const width = shade.to - shade.from;
    const points: string[] = [`${px(shade.from).toFixed(1)},${py(0).toFixed(1)}`];
    for (let i = 0; i <= steps; i += 1) {
      const x = shade.from + (width * i) / steps;
      const y = drawn(x);
      points.push(`${px(x).toFixed(1)},${py(Number.isFinite(y) ? clamp(y) : 0).toFixed(1)}`);
    }
    points.push(`${px(shade.to).toFixed(1)},${py(0).toFixed(1)}`);
    return points.join(' ');
  })();

  /* Left-endpoint rectangles: the sum the exact area is the limit of. */
  const bars = ((): { x: number; y: number; width: number }[] => {
    if (!shade?.rectangles) return [];
    const count = shade.rectangles;
    const width = (shade.to - shade.from) / count;
    const out: { x: number; y: number; width: number }[] = [];
    for (let i = 0; i < count; i += 1) {
      const left = shade.from + i * width;
      const height = drawn(left);
      if (!Number.isFinite(height)) continue;
      out.push({ x: left, y: clamp(height), width });
    }
    return out;
  })();

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="figure-svg" role="img"
         aria-label={
           roots
             ? `Graph of a polynomial with roots at ${roots.map(minus).join(', ')}`
             : `Graph of a ${curve ?? 'linear'} function${label ? `, ${label}` : ''}${
                 shade ? `, with the area under it shaded from ${minus(shade.from)} to ${minus(shade.to)}` : ''
               }`
         }>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={px(v)} y1={py(min)} x2={px(v)} y2={py(max)} stroke="var(--rule)" strokeWidth={1} />
          <line x1={px(min)} y1={py(v)} x2={px(max)} y2={py(v)} stroke="var(--rule)" strokeWidth={1} />
        </g>
      ))}

      <line x1={px(min)} y1={py(0)} x2={px(max)} y2={py(0)} stroke="var(--ink-muted)" strokeWidth={2} />
      <line x1={px(0)} y1={py(min)} x2={px(0)} y2={py(max)} stroke="var(--ink-muted)" strokeWidth={2} />

      {(asymptotes.vertical ?? []).map((x) => (
        <line key={`v${x}`} x1={px(x)} y1={py(min)} x2={px(x)} y2={py(max)}
              stroke="var(--ink-faint)" strokeWidth={2} strokeDasharray="7 5" />
      ))}
      {(asymptotes.horizontal ?? []).map((y) => (
        <line key={`h${y}`} x1={px(min)} y1={py(y)} x2={px(max)} y2={py(y)}
              stroke="var(--ink-faint)" strokeWidth={2} strokeDasharray="7 5" />
      ))}

      {region ? <polygon points={region} fill="var(--accent)" opacity={0.22} /> : null}
      {bars.map((bar, index) => (
        <rect key={`b${index}`} x={px(bar.x)} y={py(Math.max(bar.y, 0))}
              width={Math.max(0, px(bar.x + bar.width) - px(bar.x))}
              height={Math.abs(py(bar.y) - py(0))}
              fill="var(--accent)" opacity={0.22} stroke="var(--accent)" strokeWidth={1.5} />
      ))}

      {showParent
        ? runs(parent).map((path, index) => (
            <polyline key={`p${index}`} points={path} fill="none"
                      stroke="var(--ink-faint)" strokeWidth={2} strokeDasharray="5 5" />
          ))
        : null}

      {lines.map((line, index) => {
        const path = runs((x) => line.slope * x + line.intercept)[0];
        return path ? (
          <polyline key={`l${index}`} points={path} fill="none" stroke="var(--ink-muted)" strokeWidth={2}
                    strokeDasharray={line.dashed ? '6 4' : undefined} />
        ) : null;
      })}

      {runs(drawn).map((path, index) => (
        <polyline key={`c${index}`} points={path} fill="none" stroke="var(--accent)" strokeWidth={2.5} />
      ))}

      {points.map((point, index) => (
        <g key={`${point.x},${point.y},${index}`}>
          <circle cx={px(point.x)} cy={py(point.y)} r={5} fill="var(--accent)" />
          {point.label ? (
            <text x={px(point.x) + 9} y={py(point.y) - 8} className="figure-label-strong" fill="var(--accent)">
              {point.label}
            </text>
          ) : null}
        </g>
      ))}

      {label ? (
        <text x={size - padding} y={padding + 6} textAnchor="end" className="figure-label-strong" fill="var(--accent)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}

/* -------------------------------------------------------------------------- */

type PiecewisePiece = {
  slope: number;
  intercept: number;
  from: number;
  to: number;
  openStart?: boolean;
  openEnd?: boolean;
  label?: string;
};

/**
 * A piecewise-linear graph.
 *
 * The endpoints carry the meaning here, so they are drawn rather than
 * captioned: a filled dot for an end the piece owns, a hollow one for an end it
 * approaches but never reaches. An endpoint at the edge of the window gets no
 * dot at all — the piece continues past it, and a circle there would claim the
 * function stops where the paper does.
 */
function PiecewiseGraph({
  pieces,
  min = -6,
  max = 6,
  label,
}: {
  pieces: PiecewisePiece[];
  min?: number;
  max?: number;
  label?: string;
}): JSX.Element {
  const size = 300;
  const padding = 18;
  const span = max - min || 1;
  const scale = (size - 2 * padding) / span;
  const px = (x: number): number => padding + (x - min) * scale;
  const py = (y: number): number => size - padding - (y - min) * scale;
  const inside = (value: number): boolean => value >= min && value <= max;

  const ticks: number[] = [];
  for (let v = Math.ceil(min); v <= max; v += 1) ticks.push(v);

  /* Clipped to the window, so a steep piece cannot run off across the axes. */
  const segment = (piece: PiecewisePiece): { x1: number; y1: number; x2: number; y2: number } | null => {
    const at = (x: number): number => piece.slope * x + piece.intercept;
    let left = Math.max(piece.from, min);
    let right = Math.min(piece.to, max);
    if (left > right) return null;
    if (piece.slope !== 0) {
      const atValue = (y: number): number => (y - piece.intercept) / piece.slope;
      const bottom = atValue(min);
      const top = atValue(max);
      left = Math.max(left, Math.min(bottom, top));
      right = Math.min(right, Math.max(bottom, top));
    } else if (!inside(piece.intercept)) {
      return null;
    }
    if (left > right) return null;
    return { x1: left, y1: at(left), x2: right, y2: at(right) };
  };

  const dots = pieces.flatMap((piece) => {
    const at = (x: number): number => piece.slope * x + piece.intercept;
    return [
      { x: piece.from, open: piece.openStart ?? false },
      { x: piece.to, open: piece.openEnd ?? false },
    ]
      .filter((end) => inside(end.x) && inside(at(end.x)))
      .map((end) => ({ x: end.x, y: at(end.x), open: end.open }));
  });

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="figure-svg" role="img"
         aria-label={`Piecewise graph of ${pieces.length} straight pieces${label ? `, ${label}` : ''}`}>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={px(v)} y1={py(min)} x2={px(v)} y2={py(max)} stroke="var(--rule)" strokeWidth={1} />
          <line x1={px(min)} y1={py(v)} x2={px(max)} y2={py(v)} stroke="var(--rule)" strokeWidth={1} />
        </g>
      ))}

      <line x1={px(min)} y1={py(0)} x2={px(max)} y2={py(0)} stroke="var(--ink-muted)" strokeWidth={2} />
      <line x1={px(0)} y1={py(min)} x2={px(0)} y2={py(max)} stroke="var(--ink-muted)" strokeWidth={2} />

      {pieces.map((piece, index) => {
        const drawn = segment(piece);
        if (!drawn) return null;
        return (
          <line key={`s${index}`} x1={px(drawn.x1)} y1={py(drawn.y1)} x2={px(drawn.x2)} y2={py(drawn.y2)}
                stroke="var(--accent)" strokeWidth={2.5} strokeLinecap="round" />
        );
      })}

      {dots.map((dot, index) => (
        <circle key={`d${index}`} cx={px(dot.x)} cy={py(dot.y)} r={5}
                fill={dot.open ? 'var(--surface)' : 'var(--accent)'}
                stroke="var(--accent)" strokeWidth={2} />
      ))}

      {pieces.map((piece, index) =>
        piece.label ? (
          <text key={`l${index}`} x={px(Math.min(max, Math.max(min, (piece.from + piece.to) / 2)))}
                y={py(piece.slope * ((piece.from + piece.to) / 2) + piece.intercept) - 10}
                textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
            {piece.label}
          </text>
        ) : null,
      )}

      {label ? (
        <text x={size - padding} y={padding + 6} textAnchor="end" className="figure-label-strong" fill="var(--accent)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * A triangle with no right angle, in the standard lettering: side a lies
 * opposite vertex A.
 *
 * The vertices are fixed rather than computed from the labels, because the
 * labels are text ("14 cm", "x") and not always numbers. The figure's job is to
 * show *which measurement is opposite which*, and a drawing to scale is not
 * needed for that — a drawing that silently contradicts the numbers would be,
 * which is why nothing here claims to be to scale.
 */
function ObliqueTriangle({
  labels = {},
  shape = 'acute',
  showAmbiguous = false,
}: {
  labels?: { a?: string; b?: string; c?: string; A?: string; B?: string; C?: string };
  shape?: 'acute' | 'obtuse';
  showAmbiguous?: boolean;
}): JSX.Element {
  const width = 320;
  const height = 210;

  const A = { x: 40, y: 168 };
  const B = { x: 280, y: 168 };
  /* Pulling C left of A opens the angle at A past 90°. */
  const C = shape === 'obtuse' ? { x: 96, y: 44 } : { x: 190, y: 46 };
  /* The second triangle of the ambiguous case: same side lengths, other fit. */
  const C2 = { x: 236, y: 96 };

  const mid = (p: { x: number; y: number }, q: { x: number; y: number }) => ({
    x: (p.x + q.x) / 2,
    y: (p.y + q.y) / 2,
  });
  /* Nudges a side label away from the edge it names, along the outward normal. */
  const outward = (
    p: { x: number; y: number },
    q: { x: number; y: number },
    away: { x: number; y: number },
  ) => {
    const m = mid(p, q);
    const dx = m.x - away.x;
    const dy = m.y - away.y;
    const length = Math.hypot(dx, dy) || 1;
    return { x: m.x + (dx / length) * 18, y: m.y + (dy / length) * 18 + 4 };
  };

  const sideA = outward(B, C, A);
  const sideB = outward(A, C, B);
  const sideC = outward(A, B, C);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label={`${shape === 'obtuse' ? 'Obtuse' : 'Acute'} triangle ABC${showAmbiguous ? ', with a second triangle fitting the same measurements' : ''}`}>
      {showAmbiguous ? (
        <polygon points={`${A.x},${A.y} ${B.x},${B.y} ${C2.x},${C2.y}`}
                 fill="none" stroke="var(--ink-faint)" strokeWidth={2} strokeDasharray="6 5" />
      ) : null}

      <polygon points={`${A.x},${A.y} ${B.x},${B.y} ${C.x},${C.y}`}
               fill="var(--accent-soft)" stroke="var(--accent)" strokeWidth={2} />

      {([
        [A, labels.A, 'A', 14, -10],
        [B, labels.B, 'B', -12, -10],
        [C, labels.C, 'C', 0, 22],
      ] as const).map(([vertex, label, name, dx, dy]) => (
        <g key={name}>
          <text x={vertex.x + (name === 'B' ? 10 : name === 'A' ? -10 : 0)}
                y={vertex.y + (name === 'C' ? -10 : 18)}
                textAnchor="middle" className="figure-label-strong" fill="var(--accent)">
            {name}
          </text>
          {label ? (
            <text x={vertex.x + dx} y={vertex.y + dy} textAnchor="middle"
                  className="figure-label" fill="var(--ink-muted)">
              {label}
            </text>
          ) : null}
        </g>
      ))}

      {([
        [sideA, labels.a],
        [sideB, labels.b],
        [sideC, labels.c],
      ] as const).map(([at, label], index) =>
        label ? (
          <text key={index} x={at.x} y={at.y} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
            {label}
          </text>
        ) : null,
      )}
    </svg>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * The four conic sections, drawn on one grid so they can be compared.
 *
 * The hyperbola gets its asymptotes and the ellipse and hyperbola their foci on
 * request, because in both cases the interesting property is about those and
 * not about the curve alone.
 */
function Conic({
  conic,
  a = 3,
  b = 2,
  centre = { x: 0, y: 0 },
  showFoci = false,
  min = -6,
  max = 6,
  label,
}: {
  conic: 'circle' | 'ellipse' | 'parabola' | 'hyperbola';
  a?: number;
  b?: number;
  centre?: { x: number; y: number };
  showFoci?: boolean;
  min?: number;
  max?: number;
  label?: string;
}): JSX.Element {
  const size = 300;
  const padding = 16;
  const span = max - min || 1;
  const scale = (size - 2 * padding) / span;
  const px = (x: number): number => padding + (x - min) * scale;
  const py = (y: number): number => size - padding - (y - min) * scale;

  const ticks: number[] = [];
  for (let v = Math.ceil(min); v <= max; v += 1) ticks.push(v);

  /* Sampled into runs, so a branch leaving the window is not joined across it. */
  const runs = (points: { x: number; y: number }[]): string[] => {
    const paths: string[] = [];
    let current: string[] = [];
    for (const point of points) {
      if (!Number.isFinite(point.y) || point.y < min || point.y > max || point.x < min || point.x > max) {
        if (current.length > 1) paths.push(current.join(' '));
        current = [];
        continue;
      }
      current.push(`${px(point.x).toFixed(1)},${py(point.y).toFixed(1)}`);
    }
    if (current.length > 1) paths.push(current.join(' '));
    return paths;
  };

  const sample = (f: (t: number) => { x: number; y: number }, steps = 240): { x: number; y: number }[] =>
    Array.from({ length: steps + 1 }, (_, i) => f(i / steps));

  const paths: string[] = [];
  const asymptotes: { from: { x: number; y: number }; to: { x: number; y: number } }[] = [];
  const foci: { x: number; y: number }[] = [];

  if (conic === 'circle' || conic === 'ellipse') {
    const rx = conic === 'circle' ? a : a;
    const ry = conic === 'circle' ? a : b;
    paths.push(
      ...runs(
        sample((t) => ({
          x: centre.x + rx * Math.cos(2 * Math.PI * t),
          y: centre.y + ry * Math.sin(2 * Math.PI * t),
        })),
      ),
    );
    if (showFoci && conic === 'ellipse') {
      const c = Math.sqrt(Math.abs(rx * rx - ry * ry));
      if (rx >= ry) foci.push({ x: centre.x - c, y: centre.y }, { x: centre.x + c, y: centre.y });
      else foci.push({ x: centre.x, y: centre.y - c }, { x: centre.x, y: centre.y + c });
    }
  } else if (conic === 'parabola') {
    paths.push(
      ...runs(
        sample((t) => {
          const x = min + span * t;
          return { x, y: centre.y + a * (x - centre.x) ** 2 };
        }),
      ),
    );
    if (showFoci) foci.push({ x: centre.x, y: centre.y + 1 / (4 * a) });
  } else {
    for (const sign of [1, -1]) {
      paths.push(
        ...runs(
          sample((t) => {
            const u = -2.2 + 4.4 * t;
            return { x: centre.x + sign * a * Math.cosh(u), y: centre.y + b * Math.sinh(u) };
          }),
        ),
      );
    }
    for (const slope of [b / a, -b / a]) {
      asymptotes.push({
        from: { x: min, y: centre.y + slope * (min - centre.x) },
        to: { x: max, y: centre.y + slope * (max - centre.x) },
      });
    }
    if (showFoci) {
      const c = Math.sqrt(a * a + b * b);
      foci.push({ x: centre.x - c, y: centre.y }, { x: centre.x + c, y: centre.y });
    }
  }

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="figure-svg" role="img" aria-label={`A ${conic}${label ? `, ${label}` : ''}`}>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={px(v)} y1={py(min)} x2={px(v)} y2={py(max)} stroke="var(--rule)" strokeWidth={1} />
          <line x1={px(min)} y1={py(v)} x2={px(max)} y2={py(v)} stroke="var(--rule)" strokeWidth={1} />
        </g>
      ))}
      <line x1={px(min)} y1={py(0)} x2={px(max)} y2={py(0)} stroke="var(--ink-muted)" strokeWidth={2} />
      <line x1={px(0)} y1={py(min)} x2={px(0)} y2={py(max)} stroke="var(--ink-muted)" strokeWidth={2} />

      {asymptotes.map((line, index) => (
        <line key={`a${index}`} x1={px(line.from.x)} y1={py(line.from.y)} x2={px(line.to.x)} y2={py(line.to.y)}
              stroke="var(--ink-faint)" strokeWidth={2} strokeDasharray="7 5" />
      ))}

      {paths.map((path, index) => (
        <polyline key={`c${index}`} points={path} fill="none" stroke="var(--accent)" strokeWidth={2.5} />
      ))}

      <circle cx={px(centre.x)} cy={py(centre.y)} r={3.5} fill="var(--ink-muted)" />
      {foci.map((focus, index) => (
        <circle key={`f${index}`} cx={px(focus.x)} cy={py(focus.y)} r={4.5} fill="var(--accent)" />
      ))}

      {label ? (
        <text x={size - padding} y={padding + 8} textAnchor="end" className="figure-label-strong" fill="var(--accent)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Arrows in the plane.
 *
 * A vector drawn from the origin and the same vector drawn from the end of
 * another are the same vector — which is the fact the nose-to-tail picture is
 * there to make obvious, and the reason `from` exists.
 */
function VectorDiagram({
  vectors,
  min = -6,
  max = 6,
  showComponents = false,
}: {
  vectors: { x: number; y: number; label?: string; from?: { x: number; y: number }; dashed?: boolean }[];
  min?: number;
  max?: number;
  showComponents?: boolean;
}): JSX.Element {
  const size = 300;
  const padding = 16;
  const span = max - min || 1;
  const scale = (size - 2 * padding) / span;
  const px = (x: number): number => padding + (x - min) * scale;
  const py = (y: number): number => size - padding - (y - min) * scale;

  const ticks: number[] = [];
  for (let v = Math.ceil(min); v <= max; v += 1) ticks.push(v);

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="figure-svg" role="img"
         aria-label={`${vectors.length} vector${vectors.length === 1 ? '' : 's'} drawn in the plane`}>
      <defs>
        <marker id="vector-arrowhead" markerWidth={8} markerHeight={8} refX={7} refY={4} orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill="var(--accent)" />
        </marker>
      </defs>

      {ticks.map((v) => (
        <g key={v}>
          <line x1={px(v)} y1={py(min)} x2={px(v)} y2={py(max)} stroke="var(--rule)" strokeWidth={1} />
          <line x1={px(min)} y1={py(v)} x2={px(max)} y2={py(v)} stroke="var(--rule)" strokeWidth={1} />
        </g>
      ))}
      <line x1={px(min)} y1={py(0)} x2={px(max)} y2={py(0)} stroke="var(--ink-muted)" strokeWidth={2} />
      <line x1={px(0)} y1={py(min)} x2={px(0)} y2={py(max)} stroke="var(--ink-muted)" strokeWidth={2} />

      {vectors.map((vector, index) => {
        const start = vector.from ?? { x: 0, y: 0 };
        const end = { x: start.x + vector.x, y: start.y + vector.y };
        return (
          <g key={index}>
            {showComponents ? (
              <>
                <line x1={px(start.x)} y1={py(start.y)} x2={px(end.x)} y2={py(start.y)}
                      stroke="var(--ink-faint)" strokeWidth={1.5} strokeDasharray="4 4" />
                <line x1={px(end.x)} y1={py(start.y)} x2={px(end.x)} y2={py(end.y)}
                      stroke="var(--ink-faint)" strokeWidth={1.5} strokeDasharray="4 4" />
              </>
            ) : null}
            <line x1={px(start.x)} y1={py(start.y)} x2={px(end.x)} y2={py(end.y)}
                  stroke="var(--accent)" strokeWidth={2.5}
                  strokeDasharray={vector.dashed ? '6 5' : undefined}
                  markerEnd="url(#vector-arrowhead)" />
            {vector.label ? (
              <text x={px((start.x + end.x) / 2) + 10} y={py((start.y + end.y) / 2) - 8}
                    className="figure-label-strong" fill="var(--accent)">
                {vector.label}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Pascal's triangle.
 *
 * Drawn as a triangle and not as a table, because the shape carries the claim:
 * every entry sits directly below the two it is the sum of. Flattened into rows
 * of a table, that relationship has to be described in words instead of seen.
 */
function PascalTriangle({
  rows,
  highlight = [],
}: {
  rows: number;
  highlight?: { row: number; index: number }[];
}): JSX.Element {
  const cell = 42;
  const rowHeight = 34;
  const width = rows * cell;
  const height = rows * rowHeight + 12;

  const marked = new Set(highlight.map((entry) => `${entry.row},${entry.index}`));

  const values: number[][] = [];
  for (let n = 0; n < rows; n += 1) {
    const previous = values[n - 1] ?? [];
    values.push(Array.from({ length: n + 1 }, (_, k) => (n === 0 ? 1 : (previous[k - 1] ?? 0) + (previous[k] ?? 0))));
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label={`The first ${rows} rows of Pascal's triangle`}>
      {values.map((row, n) =>
        row.map((value, k) => {
          const x = width / 2 + (k - n / 2) * cell;
          const y = 22 + n * rowHeight;
          const isMarked = marked.has(`${n},${k}`);
          return (
            <g key={`${n},${k}`}>
              {isMarked ? <circle cx={x} cy={y - 5} r={15} fill="var(--accent-soft)" /> : null}
              <text x={x} y={y} textAnchor="middle"
                    className={isMarked ? 'figure-label-strong' : 'figure-label'}
                    fill={isMarked ? 'var(--accent)' : 'var(--ink-muted)'}>
                {value}
              </text>
            </g>
          );
        }),
      )}
    </svg>
  );
}

/**
 * A normal curve with its standard-deviation spine, and optionally a shaded
 * region between two z-scores.
 *
 * The height is normalised rather than scaled by 1/(σ√2π): nothing in school
 * statistics reads a density off this axis, and a curve whose peak height
 * changes with σ would suggest that it does. What the drawing has to be honest
 * about is the *horizontal* axis — the ticks are at whole standard deviations
 * and carry the data's own units, because the claim being taught is that those
 * are two labels for one axis.
 */
function NormalCurve({
  mean,
  sd,
  shade,
  marks = [-3, -2, -1, 0, 1, 2, 3],
  unitLabel,
  label,
}: {
  mean: number;
  sd: number;
  shade?: { from: number; to: number; label?: string };
  marks?: number[];
  unitLabel?: string;
  label?: string;
}): JSX.Element {
  const width = 340;
  const height = 200;
  const padding = 26;
  const baseline = height - 34;
  const span = 3.4;
  const px = (z: number): number => width / 2 + (z / span) * (width / 2 - padding);
  const py = (density: number): number => baseline - density * (baseline - padding);
  const bell = (z: number): number => Math.exp(-(z * z) / 2);

  const path = (from: number, to: number): string => {
    const points: string[] = [];
    const steps = 120;
    for (let i = 0; i <= steps; i += 1) {
      const z = from + ((to - from) * i) / steps;
      points.push(`${px(z).toFixed(1)},${py(bell(z)).toFixed(1)}`);
    }
    return points.join(' ');
  };

  const tick = (z: number): string => {
    const value = mean + z * sd;
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label={`A normal curve with mean ${mean} and standard deviation ${sd}${
           shade ? `, shaded from ${shade.from} to ${shade.to} standard deviations` : ''
         }`}>
      {shade ? (
        <polygon
          points={`${px(shade.from).toFixed(1)},${baseline} ${path(shade.from, shade.to)} ${px(shade.to).toFixed(1)},${baseline}`}
          fill="var(--accent-soft)"
        />
      ) : null}

      <line x1={padding / 2} y1={baseline} x2={width - padding / 2} y2={baseline}
            stroke="var(--ink-muted)" strokeWidth={2} />

      {marks.map((z) => (
        <g key={z}>
          <line x1={px(z)} y1={baseline} x2={px(z)} y2={py(bell(z))}
                stroke="var(--rule)" strokeWidth={z === 0 ? 2 : 1} strokeDasharray={z === 0 ? undefined : '4 4'} />
          <text x={px(z)} y={baseline + 15} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
            {tick(z)}
          </text>
          <text x={px(z)} y={baseline + 28} textAnchor="middle" className="figure-label" fill="var(--ink-faint)">
            {z === 0 ? 'μ' : `${z > 0 ? '+' : '−'}${Math.abs(z)}σ`}
          </text>
        </g>
      ))}

      <polyline points={path(-span, span)} fill="none" stroke="var(--accent)" strokeWidth={2.5} />

      {shade?.label ? (
        <text x={px((shade.from + shade.to) / 2)} y={py(0.45)} textAnchor="middle"
              className="figure-label-strong" fill="var(--accent)">
          {shade.label}
        </text>
      ) : null}

      {label ? (
        <text x={width - padding / 2} y={padding - 6} textAnchor="end" className="figure-label-strong" fill="var(--accent)">
          {label}
        </text>
      ) : null}

      {unitLabel ? (
        <text x={width / 2} y={height - 2} textAnchor="middle" className="figure-label" fill="var(--ink-faint)">
          {unitLabel}
        </text>
      ) : null}
    </svg>
  );
}

/* -------------------------------------------------------------------------- */

function VennDiagram({
  labels,
  regions,
  highlight = [],
  universeLabel,
  label,
}: {
  labels: { a: string; b: string };
  regions?: { onlyA?: string; both?: string; onlyB?: string; neither?: string };
  highlight?: ('onlyA' | 'both' | 'onlyB' | 'neither')[];
  universeLabel?: string;
  label?: string;
}): JSX.Element {
  const width = 340;
  /* Room for a title band above the circles: the set names sit in their own. */
  const height = label ? 244 : 224;
  const radius = 68;
  const universeTop = label ? 30 : 12;
  const centreY = universeTop + 106;
  const leftX = 128;
  const rightX = 212;
  const lit = (region: 'onlyA' | 'both' | 'onlyB' | 'neither'): boolean => highlight.includes(region);

  /*
   * The regions are painted by stacking clipped circles rather than by
   * computing lens outlines: two circles and their intersection is exactly what
   * `clipPath` expresses, and the arithmetic for a lens path would have to be
   * redone the moment the radii stop matching.
   */
  const ids = `venn-${leftX}-${rightX}-${radius}`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label={`A Venn diagram of ${labels.a} and ${labels.b}`}>
      <defs>
        <clipPath id={`${ids}-a`}>
          <circle cx={leftX} cy={centreY} r={radius} />
        </clipPath>
        <clipPath id={`${ids}-b`}>
          <circle cx={rightX} cy={centreY} r={radius} />
        </clipPath>
      </defs>

      <rect x={8} y={universeTop} width={width - 16} height={height - universeTop - 8} rx={10}
            fill={lit('neither') ? 'var(--accent-soft)' : 'var(--paper-sunken)'}
            stroke="var(--rule-strong)" strokeWidth={1.5} />

      {/*
        A "neither" highlight lights the ground and must then carve the circles
        back out of it. Without this the whole box goes green and the picture
        for (A ∪ B)′ says "everything" — the exact opposite of the region it is
        naming, which is the sort of error a caption cannot correct.
      */}
      {lit('neither') ? (
        <>
          <circle cx={leftX} cy={centreY} r={radius} fill="var(--paper-sunken)" />
          <circle cx={rightX} cy={centreY} r={radius} fill="var(--paper-sunken)" />
        </>
      ) : null}

      {lit('onlyA') ? <circle cx={leftX} cy={centreY} r={radius} fill="var(--accent-soft)" /> : null}
      {lit('onlyB') ? <circle cx={rightX} cy={centreY} r={radius} fill="var(--accent-soft)" /> : null}
      {lit('both') ? (
        <g clipPath={`url(#${ids}-a)`}>
          <circle cx={rightX} cy={centreY} r={radius} fill="var(--accent-soft)" />
        </g>
      ) : null}
      {/* An "only A" highlight must not spill into the overlap. */}
      {lit('onlyA') && !lit('both') ? (
        <g clipPath={`url(#${ids}-a)`}>
          <circle cx={rightX} cy={centreY} r={radius} fill="var(--paper-sunken)" />
        </g>
      ) : null}
      {lit('onlyB') && !lit('both') ? (
        <g clipPath={`url(#${ids}-b)`}>
          <circle cx={leftX} cy={centreY} r={radius} fill="var(--paper-sunken)" />
        </g>
      ) : null}

      <circle cx={leftX} cy={centreY} r={radius} fill="none" stroke="var(--accent)" strokeWidth={2.5} />
      <circle cx={rightX} cy={centreY} r={radius} fill="none" stroke="var(--accent)" strokeWidth={2.5} />

      {/* Names in their own band, clear of the title above and the circles below. */}
      <text x={leftX - radius + 4} y={centreY - radius - 10} className="figure-label-strong" fill="var(--accent)">
        {labels.a}
      </text>
      <text x={rightX + radius - 4} y={centreY - radius - 10} textAnchor="end"
            className="figure-label-strong" fill="var(--accent)">
        {labels.b}
      </text>

      {regions?.onlyA ? (
        <text x={leftX - 36} y={centreY + 5} textAnchor="middle" className="figure-label" fill="var(--ink)">
          {regions.onlyA}
        </text>
      ) : null}
      {regions?.both ? (
        <text x={(leftX + rightX) / 2} y={centreY + 5} textAnchor="middle" className="figure-label" fill="var(--ink)">
          {regions.both}
        </text>
      ) : null}
      {regions?.onlyB ? (
        <text x={rightX + 36} y={centreY + 5} textAnchor="middle" className="figure-label" fill="var(--ink)">
          {regions.onlyB}
        </text>
      ) : null}
      {regions?.neither ? (
        <text x={width - 20} y={height - 18} textAnchor="end" className="figure-label" fill="var(--ink-muted)">
          {regions.neither}
        </text>
      ) : null}

      {universeLabel ? (
        <text x={20} y={height - 18} className="figure-label" fill="var(--ink-faint)">
          {universeLabel}
        </text>
      ) : null}
      {label ? (
        <text x={width / 2} y={18} textAnchor="middle" className="figure-label-strong" fill="var(--accent)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}

function ProbabilityTree({
  branches,
  highlightPaths = [],
  label,
}: {
  branches: {
    label: string;
    probability: string;
    children?: { label: string; probability: string; outcome?: string }[];
  }[];
  highlightPaths?: [number, number][];
  label?: string;
}): JSX.Element {
  const leaves = branches.reduce((total, branch) => total + Math.max(1, branch.children?.length ?? 0), 0);
  const rowHeight = 46;
  /* A title gets its own band, so it never sits on the top branch. */
  const top = label ? 32 : 14;
  const height = Math.max(140, leaves * rowHeight + top + 14);
  const rootX = 24;
  const firstX = 122;

  /*
   * The figure is as wide as its longest name needs it to be.
   *
   * A fixed 360 with a fixed fork position put the "No disease" branch of the
   * screening tree through its own label, and squeezed the second stage into a
   * six-pixel run — four lines so nearly vertical they read as one bar across
   * the outcome column. So the fork is placed past the widest first-stage name
   * and the second stage gets a fixed run beyond it, with the width following.
   * Six pixels a character over-estimates the figure-label font slightly, which
   * is the safe direction to be wrong in.
   */
  const longestLabel = Math.max(...branches.map((branch) => branch.label.length), 0);
  const forkX = firstX + 10 + longestLabel * 6;
  const secondX = forkX + 66;
  const width = secondX + 130;

  /* Leaves are laid out evenly; a first-stage node sits at the mean of its own. */
  let cursor = 0;
  const laid = branches.map((branch) => {
    const children = branch.children ?? [];
    const count = Math.max(1, children.length);
    const ys = Array.from({ length: count }, (_, i) => top + (cursor + i + 0.5) * rowHeight);
    cursor += count;
    const y = ys.reduce((total, value) => total + value, 0) / count;
    return { branch, children, ys, y };
  });

  const rootY = (top + height - 14) / 2;
  const isLit = (i: number, j: number): boolean => highlightPaths.some(([a, b]) => a === i && b === j);
  const anyLit = highlightPaths.length > 0;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label={`A probability tree with ${branches.length} first-stage branches`}>
      {laid.map((node, i) => (
        <g key={i}>
          <line x1={rootX} y1={rootY} x2={firstX - 6} y2={node.y} stroke="var(--accent)" strokeWidth={2} />
          <text x={(rootX + firstX) / 2} y={(rootY + node.y) / 2 - 6} textAnchor="middle"
                className="figure-label" fill="var(--ink-muted)">
            {node.branch.probability}
          </text>
          <text x={firstX} y={node.y + 4} className="figure-label-strong" fill="var(--ink)">
            {node.branch.label}
          </text>

          {node.children.map((child, j) => (
            <g key={j} opacity={anyLit && !isLit(i, j) ? 0.4 : 1}>
              {/*
                Both stages are drawn in the accent unless a highlight singles
                some paths out. Colouring the second stage grey by default said
                the two stages were different kinds of thing, and the whole
                point of the diagram is that a path is one calculation.
              */}
              <line x1={forkX} y1={node.y} x2={secondX - 6} y2={node.ys[j] as number}
                    stroke={!anyLit || isLit(i, j) ? 'var(--accent)' : 'var(--rule-strong)'}
                    strokeWidth={isLit(i, j) ? 2.5 : 2} />
              <text x={(forkX + secondX) / 2} y={(node.y + (node.ys[j] as number)) / 2 - 6}
                    textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
                {child.probability}
              </text>
              <text x={secondX} y={(node.ys[j] as number) + 4} className="figure-label-strong" fill="var(--ink)">
                {child.label}
              </text>
              {child.outcome ? (
                <text x={width - 8} y={(node.ys[j] as number) + 4} textAnchor="end"
                      className="figure-label" fill={isLit(i, j) ? 'var(--accent)' : 'var(--ink-faint)'}>
                  {child.outcome}
                </text>
              ) : null}
            </g>
          ))}
        </g>
      ))}

      <circle cx={rootX} cy={rootY} r={4} fill="var(--accent)" />
      {label ? (
        <text x={width / 2} y={16} textAnchor="middle" className="figure-label-strong" fill="var(--accent)">
          {label}
        </text>
      ) : null}
    </svg>
  );
}


/* -------------------------------------------------------------------------- */

function DensityCurve({
  points,
  shade,
  marks,
  unitLabel,
  label,
}: {
  points: { x: number; y: number }[];
  shade?: { from: number; to: number; label?: string };
  marks?: number[];
  unitLabel?: string;
  label?: string;
}): JSX.Element {
  const width = 340;
  const height = 200;
  const padding = 28;
  const baseline = height - 40;

  const xs = points.map((point) => point.x);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...points.map((point) => point.y), 1e-9);
  const spanX = maxX - minX || 1;

  /*
   * The axes are scaled independently, which is the whole reason this is not a
   * `function-graph`: a density over ten minutes has a height near 0.1, and a
   * shared scale would flatten the rectangle whose area is the probability.
   */
  const px = (x: number): number => padding + ((x - minX) / spanX) * (width - 2 * padding);
  const py = (y: number): number => baseline - (y / maxY) * (baseline - padding);

  /** The density at x, by linear interpolation along the outline. */
  const densityAt = (x: number): number => {
    for (let i = 0; i < points.length - 1; i += 1) {
      const left = points[i] as { x: number; y: number };
      const right = points[i + 1] as { x: number; y: number };
      if (x >= left.x && x <= right.x) {
        if (right.x === left.x) return right.y;
        return left.y + ((right.y - left.y) * (x - left.x)) / (right.x - left.x);
      }
    }
    return 0;
  };

  const outline = points.map((point) => `${px(point.x).toFixed(1)},${py(point.y).toFixed(1)}`).join(' ');

  /* The shaded region follows the outline, so a sloping density shades a trapezium. */
  const shadePoints = ((): string => {
    if (!shade) return '';
    const from = Math.max(minX, Math.min(shade.from, shade.to));
    const to = Math.min(maxX, Math.max(shade.from, shade.to));
    const inner = points.filter((point) => point.x > from && point.x < to);
    const path = [
      { x: from, y: densityAt(from) },
      ...inner,
      { x: to, y: densityAt(to) },
    ].map((point) => `${px(point.x).toFixed(1)},${py(point.y).toFixed(1)}`);
    return `${px(from).toFixed(1)},${baseline} ${path.join(' ')} ${px(to).toFixed(1)},${baseline}`;
  })();

  const ticks = marks ?? [...new Set(xs)];

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" role="img"
         aria-label={`A probability density from ${minX} to ${maxX}${
           shade ? `, with the area from ${shade.from} to ${shade.to} shaded` : ''
         }`}>
      {shade ? <polygon points={shadePoints} fill="var(--accent-soft)" /> : null}

      <line x1={padding / 2} y1={baseline} x2={width - padding / 2} y2={baseline}
            stroke="var(--ink-muted)" strokeWidth={2} />

      {ticks.map((x) => (
        <g key={x}>
          <line x1={px(x)} y1={baseline} x2={px(x)} y2={baseline + 5} stroke="var(--ink-muted)" strokeWidth={1.5} />
          <text x={px(x)} y={baseline + 18} textAnchor="middle" className="figure-label" fill="var(--ink-muted)">
            {x}
          </text>
        </g>
      ))}

      <polyline points={outline} fill="none" stroke="var(--accent)" strokeWidth={2.5}
                strokeLinejoin="round" strokeLinecap="round" />

      {shade?.label ? (
        <text x={px((Math.max(minX, shade.from) + Math.min(maxX, shade.to)) / 2)}
              y={baseline - 14} textAnchor="middle" className="figure-label-strong" fill="var(--accent)">
          {shade.label}
        </text>
      ) : null}

      {label ? (
        <text x={width - padding / 2} y={padding - 8} textAnchor="end"
              className="figure-label-strong" fill="var(--accent)">
          {label}
        </text>
      ) : null}

      {unitLabel ? (
        <text x={width / 2} y={height - 6} textAnchor="middle" className="figure-label" fill="var(--ink-faint)">
          {unitLabel}
        </text>
      ) : null}
    </svg>
  );
}
